import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {restrictToVerticalAxis} from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import {Badge} from '@dropbox/dig-components/badge';
import {Button, IconButton} from '@dropbox/dig-components/buttons';
import {Chip} from '@dropbox/dig-components/chip';
import {LabelGroup} from '@dropbox/dig-components/label_group';
import {Menu} from '@dropbox/dig-components/menu';
import {Modal} from '@dropbox/dig-components/modal';
import {TextArea, TextInput} from '@dropbox/dig-components/text_fields';
import {Tooltip} from '@dropbox/dig-components/tooltips';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Cluster, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {
  AddLine,
  CheckmarkLine,
  DeleteLine,
  DragHandleLine,
  EditLine,
  InfoLine,
  MemberTransferLine,
  OpenLine,
  StarFill,
  StarLine,
} from '@dropbox/dig-icons/assets';
import {pulseUserAtom} from 'atoms/auth';
import {growthbookCacheAtom} from 'atoms/growthbook';
import cx from 'classnames';
import {
  EmployeeRoleLevel,
  type JiraObject,
  type MetricAttachRequest,
  type RedactedWorkstreamMetric,
  type WorkstreamDraftPolicy,
  WorkstreamMetric,
  WorkstreamMetricVisibility,
} from 'client';
import {CancelButton} from 'components/DSYS/CancelButton';
import {toLexicalJson} from 'components/DSYS/editor/utils/lexical';
import {Eyebrow} from 'components/DSYS/Eyebrow';
import {OptionalTooltip} from 'components/DSYS/OptionalTooltip';
import {StatusChip} from 'components/DSYS/StatusChip';
import {Title} from 'components/DSYS/Title';
import {CURRENT_YEAR} from 'constant';
import {isMetricDraftReasonWaived} from 'helpers/workstreamDraftPolicy';
import {useWorkstreamDraftPolicy} from 'hooks/useWorkstreamDraftPolicy';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import {
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
  type RefCallback,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import {RoadmapInitiativeLink} from 'views/roadmap/RoadmapInitiativeLink';
import {DatePickerEditor, EmployeeChip, EmployeeSelect} from 'views/workstreams/TODODSYSStuff';
import {
  getFullWorkstreamMetrics,
  getMetricCreatedAtForDraftPolicy,
  getMetricLinkedWorkstreamIds,
  getMetricOrder,
  getMetricWorkstreamLink,
  isClosedMetricStatus,
  isPrimaryMetric,
  sortWorkstreamMetrics,
} from 'views/workstreams/utils';
import {
  hasBudgetForRequiredHalf,
  isCorePillarLevel,
  isRequiredBudgetPeriod,
  shouldShowOutcomesValidation,
  useWorkstreamHealthScore,
} from 'views/workstreams/workstreamChecks';
import {
  DeliverableOutcomesValidationChecks,
  WorkstreamOutcomesValidationChecks,
} from 'views/workstreams/WorkstreamOutcomesValidation';

import {
  getMetricForecastPeriod,
  hasMetricForecastPeriodEnded,
  METRIC_FORECAST_PERIODS,
  MetricForecastGrid,
  MetricForecastHeader,
  type MetricForecastPeriod,
} from './MetricForecastGrid';
import {MetricYearSelector} from './MetricYearSelector';
import {useMetricOutcomesValidationPreviews} from './useMetricOutcomesValidationPreviews';
import {useWorkstreamManageOutcomesPickerData} from './useWorkstreamManageOutcomesPickerData';
import styles from './WorkstreamManageOutcomesModal.module.css';
import {
  getPlainTextTitle,
  isStarrableOutcome,
  normalizeExternalUrl,
} from './workstreamManageOutcomesUtils';
import {logWorkstreamMetricStatusChanged} from './workstreamMetricAnalytics';
import {buildWorkstreamMetricPickerOption} from './workstreamMetricPickerOptions';
import {
  getWorkstreamMetricStatusOption,
  WorkstreamMetricStatusMenuContent,
  type WorkstreamMetricStatusOption,
  WorkstreamMetricStatusTagContent,
} from './WorkstreamMetricStatus';
import metricStatusStyles from './WorkstreamMetricStatus.module.css';
import {
  hasInvalidMetricBaselineValue,
  hasInvalidMetricCurrentValue,
  hasInvalidMetricForecastValue,
} from './workstreamMetricValueValidation';
import metricValueValidationStyles from './workstreamMetricValueValidation.module.css';
import {
  type JiraPickerOption,
  type ManageOutcomePickerOption,
  WorkstreamOutcomePicker,
} from './WorkstreamOutcomePicker';
import {
  getOutcomeStatus,
  getOutcomeStatusUpdate,
  isTerminalOutcomeStatus,
  OutcomeStatusControl,
} from './WorkstreamOutcomeStatus';
import statusControlStyles from './WorkstreamStatusControl.module.css';

type WorkstreamMetricWithCurrentLink = WorkstreamMetric & {
  workstream_metric_link?: {
    id?: number | null;
    workstream_id?: number | null;
    order?: number | null;
    is_primary?: boolean | null;
    created_at?: string | null;
    updated_at?: string | null;
  } | null;
};

const MetricsHeaderWithTooltip = ({
  label,
  tooltipText,
  className,
  maxWidth,
}: {
  label: string;
  tooltipText: string;
  className?: string;
  maxWidth?: number;
}) => (
  <div
    className={cx(
      styles.metricsHeaderStatic,
      styles.metricsCurrentHeader,
      styles.metricsHeaderWithInfo,
      className
    )}
  >
    <span>{label}</span>
    <Tooltip title={tooltipText} placement="top" maxWidth={maxWidth}>
      <span
        className={styles.metricsHeaderInfoIcon}
        aria-label={tooltipText}
        role="img"
        tabIndex={0}
      >
        <UIIcon src={InfoLine} size="small" />
      </span>
    </Tooltip>
  </div>
);

export type ManageOutcomesTab = 'metrics' | 'outcomes' | 'milestones' | 'deliverables';
export type ManageOutcomesTriggerVariant = 'edit' | 'underline' | 'chip' | 'icon';
type VisibleManageOutcomesTab = 'metrics' | 'outcomes';
type OutcomeDraftType = 'milestone' | 'deliverable';
type ManageOutcomeDraftRow = {
  draftId: string;
  metricId: number | null;
  type: 'metric' | 'milestone' | 'deliverable';
  titleText: string;
  definition?: string;
  owner: EmployeeRoleLevel | null;
  baseline: string;
  current: string;
  url: string;
  completeDate: string | null;
  status: string | null;
  metricType: 'leading' | 'lagging' | 'other';
  isPrimary: boolean;
  isMnpi: boolean;
  order: number;
  metricForecasts: Record<
    string,
    {
      id?: number;
      period: string;
      target: string;
      budget: string;
      actual?: string | null;
      forecast_date: string | null;
    }
  >;
  originalMetric: WorkstreamMetric | null;
  isNew: boolean;
  autoAddedOwnerKey?: string | null;
  isPendingAttach: boolean;
  isLinkedMetric: boolean;
  isExpanded: boolean;
  reason: string;
};

const RestrictedMetricForecastValueInput = ({
  children,
  draftId,
  focusedMetricForecastCell,
  hasMissingRequiredBudgetError,
  periodId,
  year,
}: {
  children: ReactNode;
  draftId: string;
  focusedMetricForecastCell: string | null;
  hasMissingRequiredBudgetError: boolean;
  periodId: MetricForecastPeriod;
  year: number;
}) => {
  const requiredBudgetPeriodIds = METRIC_FORECAST_PERIODS.filter((requiredPeriodId) =>
    isRequiredBudgetPeriod(getMetricForecastPeriod(year, requiredPeriodId))
  );
  const getCellKey = (period: string, field: string) => `${draftId}:${period}:${field}`;
  const isFocusedRequiredBudgetCell = requiredBudgetPeriodIds.some(
    (requiredPeriodId) =>
      focusedMetricForecastCell ===
      getCellKey(getMetricForecastPeriod(year, requiredPeriodId), 'budget')
  );
  const showMissingRequiredBudgetError =
    hasMissingRequiredBudgetError && !isFocusedRequiredBudgetCell;

  const showBudgetRestrictionBorder =
    showMissingRequiredBudgetError && requiredBudgetPeriodIds.includes(periodId);
  if (!showBudgetRestrictionBorder) {
    return <div className={styles.metricForecastInputCell}>{children}</div>;
  }

  const firstRequiredBudgetPeriodId = requiredBudgetPeriodIds[0];
  const lastRequiredBudgetPeriodId = requiredBudgetPeriodIds[requiredBudgetPeriodIds.length - 1];
  const isFirstRequiredBudgetPeriod = periodId === firstRequiredBudgetPeriodId;
  const isLastRequiredBudgetPeriod = periodId === lastRequiredBudgetPeriodId;
  return (
    <div
      className={cx(styles.metricForecastInputCell, styles.metricForecastBudgetRestrictionCell, {
        [styles.metricForecastBudgetRestrictionCellStart]: isFirstRequiredBudgetPeriod,
        [styles.metricForecastBudgetRestrictionCellSpanTwo]:
          isFirstRequiredBudgetPeriod && requiredBudgetPeriodIds.length === 2,
        [styles.metricForecastBudgetRestrictionCellSpanThree]:
          isFirstRequiredBudgetPeriod && requiredBudgetPeriodIds.length === 3,
        [styles.metricForecastBudgetRestrictionCellEnd]: isLastRequiredBudgetPeriod,
      })}
    >
      {children}
    </div>
  );
};

type ManageOutcomeReadOnlyJiraRow = {
  id: number;
  key: string;
  titleText: string;
  owner: EmployeeRoleLevel | null;
  completeDate: string | null;
  status: string | null;
  sourceWorkstreamId: number | null;
  isPendingElevation: boolean;
};

type ManageOutcomesModalProps = {
  open: boolean;
  onClose: () => void;
  workstreamId: number;
  workstreamName?: string;
  metrics: Array<WorkstreamMetric | RedactedWorkstreamMetric>;
  jiraOutcomes?: JiraObject[];
  workstreamOwner?: EmployeeRoleLevel;
  initialTab: ManageOutcomesTab;
  autoAddRowOnOpen?: boolean;
  onActiveTabChange?: (tab: ManageOutcomesTab) => void;
  focusMetricId?: number;
  metricPeriodOptions?: string[];
  isPending?: boolean;
  onSaveMetric: (
    metricId: number,
    metricUpdate: Partial<WorkstreamMetric>,
    reason?: string,
    existingMetric?: WorkstreamMetric
  ) => Promise<WorkstreamMetric | void>;
  onAttachMetric?: (
    attachRequest: MetricAttachRequest,
    reason?: string
  ) => Promise<WorkstreamMetric | void>;
  onDeleteMetric?: (metric: WorkstreamMetric, reason: string) => Promise<void>;
  loadElevatableJiraOutcomes?: () => Promise<JiraObject[]>;
  onElevateJiraOutcome?: (jiraObjectId: number) => Promise<void>;
  onDetachJiraOutcome?: (jiraObjectId: number) => Promise<void>;
  /** Aggregate count shown while confidential metrics remain hidden from editing. */
  hiddenMnpiMetricsCount?: number;
};

type ManageOutcomesTriggerProps = Omit<ManageOutcomesModalProps, 'open' | 'onClose'> & {
  triggerVariant?: ManageOutcomesTriggerVariant;
  triggerLabel?: string;
};
type ValidationIssue =
  | 'name'
  | 'owner'
  | 'metric_type'
  | 'baseline'
  | 'budget'
  | 'due_date'
  | 'completed_url'
  | 'reason';
type FooterValidationIssue = Exclude<ValidationIssue, 'baseline' | 'budget'>;

type ManagedTriggerScope = 'metrics' | 'outcomes';
type ManagedTriggerState = {
  open: boolean;
  scope: ManagedTriggerScope | null;
  initialTab: ManageOutcomesTab;
  workstreamId?: number;
  focusMetricId?: number;
  workstreamOwner?: EmployeeRoleLevel;
  metricPeriodOptions?: string[];
};

const VISIBLE_TABS = ['metrics', 'outcomes'] as const satisfies readonly VisibleManageOutcomesTab[];
const MAX_PRIMARY_MILESTONES = 3;
const MIN_MODAL_CONTENT_HEIGHT = 240;
const MAX_MODAL_CONTENT_HEIGHT = 720;

const plainTextNode = (text: string) => ({
  detail: 0,
  format: 0,
  mode: 'normal',
  style: '',
  text,
  type: 'text',
  version: 1,
});

const toPlainTextLexicalJson = (text: string) => toLexicalJson(plainTextNode(text));

const getDraftKey = (metric: WorkstreamMetric) => `metric-${metric.id}`;
const getTempDraftKey = (type: WorkstreamMetric['type'], order: number) =>
  `new-${type}-${order}-${Math.random().toString(36).slice(2, 8)}`;
const getLinkedDraftKey = (metricId: number) =>
  `linked-${metricId}-${Math.random().toString(36).slice(2, 8)}`;

const hasLinkedMetricIndicator = (metric?: WorkstreamMetric | null) =>
  Boolean(metric && getMetricLinkedWorkstreamIds(metric).length > 1);

const shouldShowLinkedMetricIndicator = (row: ManageOutcomeDraftRow) =>
  row.isLinkedMetric || hasLinkedMetricIndicator(row.originalMetric);

type LinkedWorkstreamItemType = 'metric' | 'outcome';
type MetricReorderHandleProps = {
  attributes: ReturnType<typeof useSortable>['attributes'];
  listeners: ReturnType<typeof useSortable>['listeners'];
  setActivatorNodeRef: ReturnType<typeof useSortable>['setActivatorNodeRef'];
  isDragging: boolean;
};

const SortableMetricDraftRow = ({
  children,
  id,
}: {
  children: (handleProps: MetricReorderHandleProps) => ReactNode;
  id: string;
}) => {
  const {
    attributes,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({id});
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 3 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style}>
      {children({attributes, listeners, setActivatorNodeRef, isDragging})}
    </div>
  );
};

const LinkedWorkstreamIndicator = ({itemType}: {itemType: LinkedWorkstreamItemType}) => {
  const tooltipKey =
    itemType === 'outcome'
      ? 'outcome_linked_workstreams_tooltip'
      : 'metric_linked_workstreams_tooltip';

  return (
    <Tooltip title={t(tooltipKey).toString()}>
      <span
        className={styles.linkedMetricIndicatorCell}
        aria-label={t(tooltipKey)}
        data-testid="linked-metric-indicator"
      >
        <UIIcon
          src={MemberTransferLine}
          size="small"
          className={styles.linkedMetricIndicatorIcon}
          data-testid="linked-metric-indicator-icon"
        />
      </span>
    </Tooltip>
  );
};

const LinkedWorkstreamIndicatorCell = ({
  showIcon,
  itemType,
}: {
  showIcon?: boolean;
  itemType: LinkedWorkstreamItemType;
}) => (
  <div className={styles.linkedMetricIndicatorColumnCell}>
    {showIcon && <LinkedWorkstreamIndicator itemType={itemType} />}
  </div>
);

const JiraLinkedWorkstreamIndicator = () => (
  <Tooltip title={t('jira_linked_workstream_tooltip').toString()}>
    <span
      className={styles.linkedMetricIndicatorCell}
      aria-label={t('jira_linked_workstream_tooltip')}
      data-testid="linked-jira-indicator"
    >
      <UIIcon
        src={MemberTransferLine}
        size="small"
        className={styles.linkedMetricIndicatorIcon}
        data-testid="linked-jira-indicator-icon"
      />
    </span>
  </Tooltip>
);

const JiraLinkedWorkstreamIndicatorCell = ({showIcon}: {showIcon: boolean}) => (
  <div className={styles.linkedMetricIndicatorColumnCell}>
    {showIcon && <JiraLinkedWorkstreamIndicator />}
  </div>
);

const normalizeManageTab = (tab: ManageOutcomesTab): VisibleManageOutcomesTab =>
  tab === 'metrics' ? 'metrics' : 'outcomes';

const DEFAULT_OUTCOME_DRAFT_TYPE: OutcomeDraftType = 'milestone';

const createEmptyMetric = (
  type: WorkstreamMetric['type'],
  order: number,
  owner: EmployeeRoleLevel | null | undefined
) =>
  ({
    id: -1,
    title: '',
    definition: null,
    type,
    visibility: WorkstreamMetricVisibility.FULL,
    is_longterm: type !== 'deliverable',
    baseline: null,
    current: null,
    target: null,
    complete: false,
    complete_date: null,
    url: null,
    owner: owner ?? null,
    created_at: '',
    updated_at: '',
    is_mnpi: false,
    status: null,
    metric_type: type === 'metric' ? 'other' : null,
    metric_forecasts: [],
    workstream_metric_link: {
      id: -1,
      workstream_id: -1,
      order,
      is_primary: false,
      created_at: '',
      updated_at: '',
    },
  }) satisfies WorkstreamMetricWithCurrentLink;

const parseMetricsPeriod = (period: string) => {
  const match = /^(\d{4})(?:Q([1-4])|(EOY))$/.exec(period.trim());
  if (!match) {
    return null;
  }

  const quarter = match[2] ? Number(match[2]) : null;
  const isEoy = match[3] === 'EOY';

  return {
    year: Number(match[1]),
    quarter,
    isEoy,
    sortIndex: isEoy ? 5 : (quarter ?? 0),
  };
};

const compareMetricsPeriods = (a: string, b: string) => {
  const aParsed = parseMetricsPeriod(a);
  const bParsed = parseMetricsPeriod(b);
  if (!aParsed || !bParsed) {
    return a.localeCompare(b);
  }

  if (aParsed.year !== bParsed.year) {
    return aParsed.year - bParsed.year;
  }

  return aParsed.sortIndex - bParsed.sortIndex;
};

const getMaxModalContentHeight = () =>
  typeof window === 'undefined'
    ? MAX_MODAL_CONTENT_HEIGHT
    : Math.min(MAX_MODAL_CONTENT_HEIGHT, Math.floor(window.innerHeight * 0.7));

const buildMetricForecastDrafts = (metric: WorkstreamMetric) =>
  Object.fromEntries(
    (metric.metric_forecasts ?? []).map((forecast) => [
      forecast.period,
      {
        id: forecast.id ?? undefined,
        period: forecast.period,
        target: forecast.target ?? '',
        budget: forecast.budget ?? '',
        actual: forecast.actual ?? '',
        forecast_date: forecast.forecast_date ?? null,
      },
    ])
  );

const normalizeForecastTextValue = (value?: string | null) => value ?? '';
const normalizeMetricPayloadOptionalTextValue = (value?: string | null) =>
  value === '' ? null : (value ?? null);

const buildDraftRow = (
  metric: WorkstreamMetric,
  workstreamId: number,
  isExpanded = false
): ManageOutcomeDraftRow => ({
  draftId: getDraftKey(metric),
  metricId: metric.id,
  type: metric.type as ManageOutcomeDraftRow['type'],
  titleText: getPlainTextTitle(metric.title),
  definition: metric.definition ?? '',
  owner: metric.owner ?? null,
  baseline: metric.baseline ?? '',
  current: metric.current ?? '',
  url: metric.url ?? '',
  completeDate: metric.complete_date ?? null,
  status: metric.type === 'metric' ? (metric.status ?? 'no_status') : getOutcomeStatus(metric),
  metricType:
    metric.metric_type === 'leading' || metric.metric_type === 'lagging'
      ? metric.metric_type
      : 'other',
  isPrimary: isPrimaryMetric(metric, workstreamId),
  isMnpi: Boolean(metric.is_mnpi),
  order: getMetricOrder(metric, workstreamId),
  metricForecasts: buildMetricForecastDrafts(metric),
  originalMetric: metric,
  isNew: false,
  isPendingAttach: false,
  isLinkedMetric: hasLinkedMetricIndicator(metric),
  isExpanded,
  reason: '',
});

const buildEmptyDraftRow = (
  type: ManageOutcomeDraftRow['type'],
  order: number,
  owner: EmployeeRoleLevel | null | undefined,
  draftId = getTempDraftKey(type, order)
): ManageOutcomeDraftRow => {
  const metric = createEmptyMetric(type, order, owner);
  return {
    draftId,
    metricId: null,
    type,
    titleText: '',
    definition: '',
    owner: owner ?? null,
    baseline: '',
    current: '',
    url: '',
    completeDate: null,
    status: type === 'metric' ? 'no_status' : 'open',
    metricType: 'other',
    isPrimary: false,
    isMnpi: false,
    order,
    metricForecasts: buildMetricForecastDrafts(metric),
    originalMetric: null,
    isNew: true,
    isPendingAttach: false,
    isLinkedMetric: false,
    isExpanded: true,
    reason: '',
  };
};

const buildSelectedExistingDraftRow = (
  metric: WorkstreamMetric,
  workstreamId: number,
  order: number
): ManageOutcomeDraftRow => ({
  ...buildDraftRow(metric, workstreamId, true),
  draftId: getLinkedDraftKey(metric.id),
  order,
  isPrimary: false,
  isPendingAttach: true,
  isLinkedMetric: hasLinkedMetricIndicator(metric),
});

const buildReadOnlyJiraRow = (
  item: JiraObject,
  isPendingElevation = false
): ManageOutcomeReadOnlyJiraRow => ({
  id: item.id,
  key: item.key,
  titleText: getPlainTextTitle(item.title),
  owner: item.owner ?? null,
  completeDate: item.complete_date ?? null,
  status: item.status ?? null,
  sourceWorkstreamId: item.workstream_id ?? null,
  isPendingElevation,
});

const isLinkedJiraOutcome = (row: ManageOutcomeReadOnlyJiraRow, workstreamId: number) =>
  row.isPendingElevation ||
  (row.sourceWorkstreamId != null && row.sourceWorkstreamId !== workstreamId);

const buildJiraPickerOption = (jiraObject: JiraObject): JiraPickerOption => ({
  kind: 'jira',
  optionId: `jira:${jiraObject.id}`,
  title: getPlainTextTitle(jiraObject.title),
  subtitle: jiraObject.owner?.name ?? jiraObject.key,
  jiraObject,
});

const getDraftTab = (row: ManageOutcomeDraftRow): VisibleManageOutcomesTab =>
  row.type === 'metric' ? 'metrics' : 'outcomes';

const getTabCounts = (rows: ManageOutcomeDraftRow[]) => ({
  metrics: rows.filter((row) => row.type === 'metric').length,
  outcomes: rows.filter((row) => row.type === 'milestone' || row.type === 'deliverable').length,
});

const getJiraOutcomeStatusChipProps = (status: string | null | undefined) => {
  const jiraStatus = (status ?? '').trim();

  switch (jiraStatus) {
    case 'On Track':
      return {status: jiraStatus, asStatus: 'on_track'};
    case 'At  Risk':
      return {status: jiraStatus, asStatus: 'at_risk'};
    case 'Delayed':
    case 'Deprioritized':
      return {status: jiraStatus, asStatus: 'off_track'};
    case 'Done':
    case 'Completed':
      return {status: jiraStatus, asStatus: 'complete'};
    case 'To Do':
      return {status: jiraStatus, asStatus: 'no_status'};
    default:
      return {status: jiraStatus || 'not_started', asStatus: 'no_status'};
  }
};

const buildMetricsYears = (rows: ManageOutcomeDraftRow[], metricPeriodOptions?: string[]) => {
  const years = new Set<number>();

  years.add(CURRENT_YEAR - 1);
  years.add(CURRENT_YEAR);

  for (const period of metricPeriodOptions ?? []) {
    const parsed = parseMetricsPeriod(period);
    if (parsed) {
      years.add(parsed.year);
    }
  }

  for (const row of rows) {
    if (row.type !== 'metric') continue;
    for (const period of Object.keys(row.metricForecasts)) {
      const parsed = parseMetricsPeriod(period);
      if (parsed) {
        years.add(parsed.year);
      }
    }
  }

  if (!years.size) {
    years.add(CURRENT_YEAR);
  }

  const selectableYears = [...years].sort((a, b) => a - b);
  return selectableYears.length ? selectableYears : [CURRENT_YEAR];
};

const isNorthStarMetricType = (metricType: ManageOutcomeDraftRow['metricType']) =>
  metricType === 'leading' || metricType === 'lagging';

const isNorthStarMetric = (row: ManageOutcomeDraftRow) =>
  row.type === 'metric' && row.isPrimary && isNorthStarMetricType(row.metricType);

const orderMetricDraftRows = (rows: ManageOutcomeDraftRow[]) => {
  const sortedRows = [...rows].sort((a, b) => {
    const aClosed = isClosedMetricStatus(a.status);
    const bClosed = isClosedMetricStatus(b.status);
    if (aClosed !== bClosed) {
      return aClosed ? 1 : -1;
    }
    return a.order - b.order;
  });
  const primaryRows = sortedRows.filter(isNorthStarMetric);
  const remainingRows = sortedRows.filter((row) => !isNorthStarMetric(row));
  return primaryRows.length > 0 ? [...primaryRows, ...remainingRows] : sortedRows;
};

const canReorderMetricDraftRows = (source: ManageOutcomeDraftRow, target: ManageOutcomeDraftRow) =>
  isNorthStarMetric(source) === isNorthStarMetric(target);

const orderOutcomeDraftRows = (rows: ManageOutcomeDraftRow[]) =>
  [...rows].sort((a, b) => {
    const aPrimaryRank = isStarrableOutcome(a) && a.isPrimary ? 0 : 1;
    const bPrimaryRank = isStarrableOutcome(b) && b.isPrimary ? 0 : 1;

    if (aPrimaryRank !== bPrimaryRank) {
      return aPrimaryRank - bPrimaryRank;
    }

    const aTime = a.completeDate ? new Date(a.completeDate).getTime() : Infinity;
    const bTime = b.completeDate ? new Date(b.completeDate).getTime() : Infinity;

    if (aTime !== bTime) {
      return aTime - bTime;
    }

    if (a.order !== b.order) {
      return a.order - b.order;
    }

    return a.draftId.localeCompare(b.draftId);
  });

const ManageOutcomeTitleField = ({
  value,
  isInvalid = false,
  disabled = false,
  placeholder,
  fieldRef,
  onChange,
  onFocus,
  onBlur,
}: {
  value: string;
  isInvalid?: boolean;
  disabled?: boolean;
  placeholder: string;
  fieldRef?: RefCallback<HTMLElement>;
  onChange: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
}) => {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const setTextareaRef = useCallback(
    (node: HTMLTextAreaElement | null) => {
      textareaRef.current = node;
      fieldRef?.(node);
    },
    [fieldRef]
  );

  const resizeTextarea = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.overflowY = 'hidden';
    textarea.style.height = '0px';
    textarea.style.height = `${textarea.scrollHeight}px`;
  };

  useLayoutEffect(() => {
    resizeTextarea();
  }, [value]);

  return (
    <TextArea
      ref={setTextareaRef}
      rows={1}
      isInvalid={isInvalid}
      disabled={disabled}
      value={value}
      className={cx(styles.titleInput, styles.titleTextArea)}
      onChange={(event) => onChange(event.currentTarget.value)}
      onFocus={onFocus}
      onBlur={onBlur}
      onInput={resizeTextarea}
      placeholder={placeholder}
    />
  );
};

const formatTabLabel = (tab: ManageOutcomesTab) => {
  if (tab === 'metrics') return t('metrics').toString();
  return t('milestones').toString();
};

const formatManageEyebrowLabel = (tab: ManageOutcomesTab) => {
  if (tab === 'metrics') return t('edit_metrics').toString();
  return t('edit_milestones').toString();
};

const formatManageSubtitle = (tab: ManageOutcomesTab, count: number) => {
  if (tab === 'metrics') return t('manage_metrics_count', {count}).toString();
  return t('manage_milestones_count', {count}).toString();
};

const getMetricTypeLabel = (metricType: ManageOutcomeDraftRow['metricType']) =>
  metricType === 'leading'
    ? t('lead').toString()
    : metricType === 'lagging'
      ? t('lag').toString()
      : t('other').toString();

const normalizeMetricStatus = (row: ManageOutcomeDraftRow) => row.status ?? 'no_status';

const hasPrimaryNorthStarMetric = (rows: ManageOutcomeDraftRow[], excludingDraftId?: string) =>
  rows.some(
    (row) =>
      row.draftId !== excludingDraftId &&
      row.type === 'metric' &&
      row.isPrimary &&
      isNorthStarMetricType(row.metricType)
  );

const hasReachedPrimaryMilestoneLimit = (
  rows: ManageOutcomeDraftRow[],
  excludingDraftId?: string
) =>
  rows.filter((row) => row.draftId !== excludingDraftId && isStarrableOutcome(row) && row.isPrimary)
    .length >= MAX_PRIMARY_MILESTONES;

const getPinDisabledTooltipKey = (
  row: ManageOutcomeDraftRow,
  rows: ManageOutcomeDraftRow[]
): 'manage_outcomes_pin_limit_north_star' | 'manage_outcomes_pin_limit_milestone' | null => {
  if (isStarrableOutcome(row)) {
    if (row.isPrimary) {
      return null;
    }

    return hasReachedPrimaryMilestoneLimit(rows, row.draftId)
      ? 'manage_outcomes_pin_limit_milestone'
      : null;
  }

  if (row.type !== 'metric') {
    return null;
  }

  if (row.isPrimary) {
    return null;
  }

  if (!isNorthStarMetricType(row.metricType)) {
    return 'manage_outcomes_pin_limit_north_star';
  }

  if (hasPrimaryNorthStarMetric(rows, row.draftId)) {
    return 'manage_outcomes_pin_limit_north_star';
  }

  return null;
};

const canPinMetricRow = (row: ManageOutcomeDraftRow, rows: ManageOutcomeDraftRow[]) => {
  if (isStarrableOutcome(row)) {
    if (row.isPrimary) {
      return true;
    }

    return !hasReachedPrimaryMilestoneLimit(rows, row.draftId);
  }

  if (row.type !== 'metric') {
    return false;
  }

  if (row.isPrimary) {
    return true;
  }

  if (!isNorthStarMetricType(row.metricType)) {
    return false;
  }

  return !hasPrimaryNorthStarMetric(rows, row.draftId);
};

const getSelectedOwner = (
  owner: EmployeeRoleLevel | null | undefined,
  workstreamOwner?: EmployeeRoleLevel
) => owner ?? workstreamOwner ?? null;

const getInitialOwnerSelection = (
  row: ManageOutcomeDraftRow,
  workstreamOwner?: EmployeeRoleLevel
) => getSelectedOwner(row.originalMetric?.owner ?? null, workstreamOwner);

const getCurrentOwnerSelection = (
  row: ManageOutcomeDraftRow,
  workstreamOwner?: EmployeeRoleLevel
) => getSelectedOwner(row.owner, workstreamOwner);

const hasTextChangedAfterInitialSelection = (initial: string | null | undefined, next: string) => {
  const initialValue = (initial ?? '').trim();
  return initialValue !== '' && next.trim() !== initialValue;
};

const hasOwnerChangedAfterInitialSelection = (
  row: ManageOutcomeDraftRow,
  workstreamOwner?: EmployeeRoleLevel
) => {
  const initialOwner = getInitialOwnerSelection(row, workstreamOwner);
  if (!initialOwner) return false;

  const currentOwner = getCurrentOwnerSelection(row, workstreamOwner);
  const initialOwnerKey = initialOwner.user_id ?? initialOwner.ldap ?? null;
  const currentOwnerKey = currentOwner?.user_id ?? currentOwner?.ldap ?? null;
  return currentOwnerKey !== initialOwnerKey;
};

const hasAutoAddedOwnerChanged = (row: ManageOutcomeDraftRow) => {
  const currentOwnerKey = row.owner?.user_id ?? row.owner?.ldap ?? null;
  return currentOwnerKey !== row.autoAddedOwnerKey;
};

const hasForecastValueChangedAfterInitialSelection = (
  initial: string | null | undefined,
  next: string | null | undefined
) => {
  const initialValue = normalizeForecastTextValue(initial);
  if (initialValue === '') return false;
  return normalizeForecastTextValue(next) !== initialValue;
};

const hasDateChangedAfterInitialSelection = (
  initial: string | null | undefined,
  next: string | null
) => (initial ?? null) !== null && (next ?? null) !== (initial ?? null);

const hasRowContentChanges = (row: ManageOutcomeDraftRow, workstreamId: number) => {
  if (row.isNew) {
    return (
      row.titleText.trim().length > 0 ||
      row.baseline.trim().length > 0 ||
      row.current.trim().length > 0 ||
      row.completeDate !== null ||
      row.url.trim().length > 0 ||
      Boolean(row.definition?.trim()) ||
      (row.autoAddedOwnerKey !== undefined ? hasAutoAddedOwnerChanged(row) : row.owner !== null) ||
      Object.values(row.metricForecasts).some(
        (forecast) =>
          Boolean(forecast.target) ||
          Boolean(forecast.budget) ||
          Boolean(forecast.actual) ||
          Boolean(forecast.forecast_date)
      )
    );
  }

  if (!row.originalMetric) return false;
  const original = row.originalMetric;
  const originalTitle = getPlainTextTitle(original.title).trim();
  const metricForecastsChanged = (() => {
    const originalForecastDrafts = buildMetricForecastDrafts(original);
    const draftPeriods = new Set([
      ...Object.keys(row.metricForecasts),
      ...Object.keys(originalForecastDrafts),
    ]);
    return [...draftPeriods].some((period) => {
      const draft = row.metricForecasts[period];
      const existing = originalForecastDrafts[period];
      return (
        normalizeForecastTextValue(draft?.target) !==
          normalizeForecastTextValue(existing?.target) ||
        normalizeForecastTextValue(draft?.budget) !==
          normalizeForecastTextValue(existing?.budget) ||
        normalizeForecastTextValue(draft?.actual) !==
          normalizeForecastTextValue(existing?.actual) ||
        (draft?.forecast_date ?? null) !== (existing?.forecast_date ?? null)
      );
    });
  })();

  return (
    row.titleText.trim() !== originalTitle ||
    row.type !== original.type ||
    row.baseline !== (original.baseline ?? '') ||
    row.current !== (original.current ?? '') ||
    row.url.trim() !== (original.url ?? '').trim() ||
    row.definition?.trim() !== (original.definition ?? '').trim() ||
    (row.completeDate ?? null) !== (original.complete_date ?? null) ||
    (row.type === 'metric'
      ? normalizeMetricStatus(row)
      : getOutcomeStatus({status: row.status})) !==
      (original.type === 'metric'
        ? (original.status ?? 'no_status')
        : getOutcomeStatus(original)) ||
    row.metricType !==
      (original.metric_type === 'leading' || original.metric_type === 'lagging'
        ? original.metric_type
        : 'other') ||
    row.isPrimary !== isPrimaryMetric(original, workstreamId) ||
    row.order !== getMetricOrder(original, workstreamId) ||
    row.isMnpi !== Boolean(original.is_mnpi) ||
    (row.owner?.user_id ?? null) !== (original.owner?.user_id ?? null) ||
    metricForecastsChanged
  );
};

const isDirty = (row: ManageOutcomeDraftRow, workstreamId: number) => {
  return row.isPendingAttach || hasRowContentChanges(row, workstreamId);
};

const shouldEnforceMetricRestrictionsForRow = (
  row: ManageOutcomeDraftRow,
  workstreamId: number,
  enforceMetricRestrictions: boolean
) =>
  enforceMetricRestrictions &&
  isDirty(row, workstreamId) &&
  row.type === 'metric' &&
  !isClosedMetricStatus(row.status);

const canShowInvalidMetricValueUnderline = (
  row: ManageOutcomeDraftRow,
  workstreamId: number,
  enforceMetricRestrictions: boolean
) =>
  enforceMetricRestrictions &&
  isDirty(row, workstreamId) &&
  row.type === 'metric' &&
  !isClosedMetricStatus(row.status);

const hasMetricBaselineRestrictionError = (
  row: ManageOutcomeDraftRow,
  workstreamId: number,
  enforceMetricRestrictions: boolean
) =>
  shouldEnforceMetricRestrictionsForRow(row, workstreamId, enforceMetricRestrictions) &&
  !row.baseline.trim();

const hasMetricBudgetRestrictionError = (
  row: ManageOutcomeDraftRow,
  workstreamId: number,
  enforceMetricRestrictions: boolean
) =>
  shouldEnforceMetricRestrictionsForRow(row, workstreamId, enforceMetricRestrictions) &&
  !hasBudgetForRequiredHalf(row);

const getRowValidationIssue = (
  row: ManageOutcomeDraftRow,
  workstreamId: number,
  workstreamOwner: EmployeeRoleLevel | undefined,
  draftPolicy: WorkstreamDraftPolicy | undefined,
  rows: ManageOutcomeDraftRow[],
  enforceMetricRestrictions: boolean
): ValidationIssue | null => {
  // Untouched legacy rows may be incomplete; validate only rows included in this save.
  if (!isDirty(row, workstreamId)) return null;
  const hasBaselineError = hasMetricBaselineRestrictionError(
    row,
    workstreamId,
    enforceMetricRestrictions
  );
  const hasBudgetError = hasMetricBudgetRestrictionError(
    row,
    workstreamId,
    enforceMetricRestrictions
  );
  if (!row.titleText.trim()) return 'name';
  if (!getCurrentOwnerSelection(row, workstreamOwner)) return 'owner';
  if (row.isNew && row.type === 'metric' && row.metricType === 'other') return 'metric_type';
  if (hasBaselineError) return 'baseline';
  if (hasBudgetError) return 'budget';
  if (row.type !== 'metric' && !row.completeDate) return 'due_date';
  if (hasMissingReasonError(row, workstreamId, workstreamOwner, draftPolicy, rows)) {
    return 'reason';
  }

  return null;
};

const getValidationIssueMessage = (issue: ValidationIssue) => {
  // TODO (@youcef): Disabling validation messages to reduce noise
  return null;
  switch (issue) {
    case 'name':
      return t('manage_outcomes_error_name_required').toString();
    case 'owner':
      return t('manage_outcomes_error_dri_required').toString();
    case 'metric_type':
      return t('manage_outcomes_error_metric_type_required').toString();
    case 'due_date':
      return t('manage_outcomes_error_due_date_required').toString();
    case 'completed_url':
      return t('manage_outcomes_error_completed_milestone_url_required').toString();
    case 'reason':
      return t('manage_outcomes_error_reason_required').toString();
  }
};

const FOOTER_VALIDATION_ISSUE_ORDER: readonly FooterValidationIssue[] = [
  'name',
  'owner',
  'metric_type',
  'due_date',
  'completed_url',
  'reason',
];

const getFooterValidationLabel = (tab: VisibleManageOutcomesTab, count: number) =>
  tab === 'metrics'
    ? t(count === 1 ? 'metric' : 'metrics')
        .toString()
        .toLowerCase()
    : t(count === 1 ? 'milestone' : 'milestones')
        .toString()
        .toLowerCase();

const getFooterValidationMessageKey = (issue: FooterValidationIssue) => {
  switch (issue) {
    case 'name':
      return 'manage_outcomes_footer_needs_name';
    case 'owner':
      return 'manage_outcomes_footer_needs_dri';
    case 'metric_type':
      return 'manage_outcomes_footer_needs_metric_type';
    case 'due_date':
      return 'manage_outcomes_footer_needs_due_date';
    case 'completed_url':
      return 'manage_outcomes_footer_needs_url';
    case 'reason':
      return 'manage_outcomes_footer_requires_rationale';
  }
};

const getFooterValidationSummary = (
  rows: ManageOutcomeDraftRow[],
  workstreamId: number,
  workstreamOwner: EmployeeRoleLevel | undefined,
  draftPolicy: WorkstreamDraftPolicy | undefined,
  enforceMetricRestrictions: boolean
) => {
  const issueCounts = new Map<string, number>();

  rows.forEach((row) => {
    const issue = getRowValidationIssue(
      row,
      workstreamId,
      workstreamOwner,
      draftPolicy,
      rows,
      enforceMetricRestrictions
    );
    if (!issue) return;
    const tab = getDraftTab(row);
    const issueKey = `${issue}:${tab}`;
    issueCounts.set(issueKey, (issueCounts.get(issueKey) ?? 0) + 1);
  });

  const messages = FOOTER_VALIDATION_ISSUE_ORDER.flatMap((issue) =>
    VISIBLE_TABS.flatMap((tab) => {
      const count = issueCounts.get(`${issue}:${tab}`);
      if (!count) return [];

      return [
        `${count} ${getFooterValidationLabel(tab, count)} ${t(
          getFooterValidationMessageKey(issue),
          {count}
        ).toString()}`,
      ];
    })
  );

  return messages.length ? messages.join(', ') : null;
};

const requiresReason = (
  row: ManageOutcomeDraftRow,
  workstreamId: number,
  workstreamOwner: EmployeeRoleLevel | undefined,
  draftPolicy: WorkstreamDraftPolicy | undefined,
  rows: ManageOutcomeDraftRow[]
) => {
  if (row.isNew || !row.originalMetric) return false;
  if (
    isMetricDraftReasonWaived(
      draftPolicy,
      getMetricCreatedAtForDraftPolicy(row.originalMetric, workstreamId)
    )
  ) {
    return false;
  }
  const original = row.originalMetric;

  const originalForecasts = buildMetricForecastDrafts(original);
  const forecastEdited = Object.entries(originalForecasts).some(([period, forecast]) => {
    const draft = row.metricForecasts[period];
    if (!draft) return false;
    return (
      hasForecastValueChangedAfterInitialSelection(forecast.target, draft.target) ||
      hasForecastValueChangedAfterInitialSelection(forecast.budget, draft.budget) ||
      hasForecastValueChangedAfterInitialSelection(forecast.actual, draft.actual)
    );
  });

  const nextPrimaryState =
    row.type === 'metric' ? row.isPrimary : isStarrableOutcome(row) ? row.isPrimary : false;
  const isFirstNorthStarDesignation =
    row.type === 'metric' &&
    !isPrimaryMetric(original, workstreamId) &&
    nextPrimaryState &&
    !rows.some(
      (candidate) =>
        candidate.draftId !== row.draftId &&
        candidate.type === 'metric' &&
        candidate.originalMetric != null &&
        isPrimaryMetric(candidate.originalMetric, workstreamId)
    );
  return (
    hasTextChangedAfterInitialSelection(getPlainTextTitle(original.title), row.titleText) ||
    hasTextChangedAfterInitialSelection(original.baseline, row.baseline) ||
    hasOwnerChangedAfterInitialSelection(row, workstreamOwner) ||
    (row.type !== 'metric' &&
      hasDateChangedAfterInitialSelection(original.complete_date, row.completeDate)) ||
    ((row.type === 'metric' || isStarrableOutcome(row)) &&
      isPrimaryMetric(original, workstreamId) !== nextPrimaryState &&
      !isFirstNorthStarDesignation) ||
    forecastEdited
  );
};

const validateRow = (
  row: ManageOutcomeDraftRow,
  workstreamId: number,
  workstreamOwner: EmployeeRoleLevel | undefined,
  draftPolicy: WorkstreamDraftPolicy | undefined,
  rows: ManageOutcomeDraftRow[],
  enforceMetricRestrictions: boolean
) => {
  const issue = getRowValidationIssue(
    row,
    workstreamId,
    workstreamOwner,
    draftPolicy,
    rows,
    enforceMetricRestrictions
  );
  return issue ? getValidationIssueMessage(issue) : null;
};

const hasMissingReasonError = (
  row: ManageOutcomeDraftRow,
  workstreamId: number,
  workstreamOwner: EmployeeRoleLevel | undefined,
  draftPolicy: WorkstreamDraftPolicy | undefined,
  rows: ManageOutcomeDraftRow[]
) => requiresReason(row, workstreamId, workstreamOwner, draftPolicy, rows) && !row.reason.trim();

const hasMissingNameError = (row: ManageOutcomeDraftRow, workstreamId: number) =>
  isDirty(row, workstreamId) && !row.titleText.trim();

const hasMissingOwnerError = (
  row: ManageOutcomeDraftRow,
  workstreamId: number,
  workstreamOwner?: EmployeeRoleLevel
) => isDirty(row, workstreamId) && !getCurrentOwnerSelection(row, workstreamOwner);

const hasMissingMetricTypeError = (row: ManageOutcomeDraftRow, workstreamId: number) =>
  isDirty(row, workstreamId) && row.isNew && row.type === 'metric' && row.metricType === 'other';

const getSerializedDraftTitle = (row: ManageOutcomeDraftRow) => {
  if (
    row.originalMetric &&
    !hasTextChangedAfterInitialSelection(getPlainTextTitle(row.originalMetric.title), row.titleText)
  ) {
    return row.originalMetric.title;
  }

  return toPlainTextLexicalJson(row.titleText.trim());
};

const buildMetricPayload = (
  row: ManageOutcomeDraftRow,
  workstreamId: number,
  isGtmPilotChangesEnabled: boolean
): Partial<WorkstreamMetric> => {
  const sortedForecasts = Object.values(row.metricForecasts)
    .filter(
      (forecast) =>
        Boolean(forecast.target) ||
        Boolean(forecast.budget) ||
        Boolean(forecast.actual) ||
        Boolean(forecast.forecast_date) ||
        Boolean(forecast.id)
    )
    .sort((a, b) => compareMetricsPeriods(a.period, b.period))
    .map((forecast) => ({
      id: forecast.id ?? null,
      period: forecast.period,
      budget: normalizeMetricPayloadOptionalTextValue(forecast.budget),
      target: forecast.target ?? '',
      actual: normalizeMetricPayloadOptionalTextValue(forecast.actual),
      forecast_date: forecast.forecast_date ?? null,
    }));
  const firstForecast = sortedForecasts[0];

  if (row.type === 'metric') {
    const metricLink = row.originalMetric
      ? getMetricWorkstreamLink(row.originalMetric, workstreamId)
      : null;
    return {
      title: getSerializedDraftTitle(row),
      type: row.type,
      owner: row.owner,
      baseline: row.baseline || null,
      current: row.current || null,
      target: (firstForecast?.target as string | null | undefined) ?? null,
      complete: false,
      complete_date: firstForecast?.forecast_date ?? null,
      url: row.url || null,
      ...(isGtmPilotChangesEnabled ? {definition: row.definition || null} : {}),
      is_longterm: true,
      status: normalizeMetricStatus(row),
      metric_type: row.metricType,
      is_mnpi: row.isMnpi,
      metric_forecasts: sortedForecasts as WorkstreamMetric['metric_forecasts'],
      workstream_metric_link: {
        ...(metricLink?.id != null ? {id: metricLink.id} : {}),
        order: row.order,
        is_primary: isNorthStarMetric(row),
      } as WorkstreamMetricWithCurrentLink['workstream_metric_link'],
    } as Partial<WorkstreamMetricWithCurrentLink>;
  }

  if (row.type === 'milestone') {
    const metricLink = row.originalMetric
      ? getMetricWorkstreamLink(row.originalMetric, workstreamId)
      : null;
    const outcomeStatus = getOutcomeStatusUpdate(getOutcomeStatus({status: row.status}));
    return {
      title: getSerializedDraftTitle(row),
      type: row.type,
      owner: row.owner,
      current: null,
      target: null,
      complete: outcomeStatus.complete,
      complete_date: row.completeDate,
      url: row.url || null,
      is_longterm: true,
      status: outcomeStatus.status,
      metric_type: null,
      is_mnpi: row.isMnpi,
      metric_forecasts: [],
      workstream_metric_link: {
        ...(metricLink?.id != null ? {id: metricLink.id} : {}),
        order: row.order,
        is_primary: row.isPrimary,
      } as WorkstreamMetricWithCurrentLink['workstream_metric_link'],
    } as Partial<WorkstreamMetricWithCurrentLink>;
  }

  const outcomeStatus = getOutcomeStatusUpdate(getOutcomeStatus({status: row.status}));
  const metricLink = row.originalMetric
    ? getMetricWorkstreamLink(row.originalMetric, workstreamId)
    : null;
  return {
    title: getSerializedDraftTitle(row),
    type: row.type,
    owner: row.owner,
    current: null,
    target: null,
    complete: outcomeStatus.complete,
    complete_date: row.completeDate,
    url: row.url || null,
    is_longterm: false,
    status: outcomeStatus.status,
    metric_type: null,
    is_mnpi: row.isMnpi,
    metric_forecasts: [],
    workstream_metric_link: {
      ...(metricLink?.id != null ? {id: metricLink.id} : {}),
      order: row.order,
      is_primary: row.isPrimary,
    } as WorkstreamMetricWithCurrentLink['workstream_metric_link'],
  } as Partial<WorkstreamMetricWithCurrentLink>;
};

const getRowLabel = (row: ManageOutcomeDraftRow) => {
  if (row.type === 'metric') return t('metric').toString();
  if (row.type === 'milestone' || row.type === 'deliverable') return t('milestone').toString();
  return '';
};

const getOriginalOutcomeStatusValue = (row: ManageOutcomeDraftRow) =>
  row.originalMetric && row.originalMetric.type !== 'metric'
    ? getOutcomeStatus(row.originalMetric)
    : null;

const wasOutcomeTerminalOnOpen = (row: ManageOutcomeDraftRow) => {
  const status = getOriginalOutcomeStatusValue(row);
  return status === 'completed' || status === 'missed';
};

const DELIVERABLE_URL_INPUT_MIN_WIDTH_PX = 200;
const DELIVERABLE_URL_INPUT_MAX_WIDTH_PX = 600;

const getDeliverableUrlNaturalWidthPx = (url: string) =>
  Math.max(DELIVERABLE_URL_INPUT_MIN_WIDTH_PX, url.trim().length * 7 + 32);
const getDeliverableUrlInputWidthPx = (url: string) =>
  Math.min(DELIVERABLE_URL_INPUT_MAX_WIDTH_PX, getDeliverableUrlNaturalWidthPx(url));
const getDeliverableUrlInputWidth = (url: string) => `${getDeliverableUrlInputWidthPx(url)}px`;
const shouldRenderDeliverableUrlIconOutside = (url: string) =>
  getDeliverableUrlNaturalWidthPx(url) > DELIVERABLE_URL_INPUT_MAX_WIDTH_PX;
const getDeliverableUrlIconOffset = (url: string) =>
  `${Math.min(getDeliverableUrlInputWidthPx(url) - 24, Math.max(34, url.trim().length * 7 + 22))}px`;

const MetricTypeMenu = ({
  metricType,
  isMetricNew,
  onSelect,
  disabled = false,
  isInvalid = false,
  allowOtherOption = true,
}: {
  metricType: ManageOutcomeDraftRow['metricType'];
  isMetricNew: boolean;
  onSelect: (metricType: ManageOutcomeDraftRow['metricType']) => void;
  disabled?: boolean;
  isInvalid?: boolean;
  allowOtherOption?: boolean;
}) => {
  const options: ManageOutcomeDraftRow['metricType'][] = allowOtherOption
    ? ['leading', 'lagging', 'other']
    : ['leading', 'lagging'];
  const label =
    isMetricNew && metricType === 'other'
      ? t('manage_outcomes_select_metric_type').toString()
      : getMetricTypeLabel(metricType);

  return (
    <Menu.Wrapper shouldPropagateClickOutsideMouseEvents>
      {({getContentProps, getTriggerProps}) => (
        <>
          <Button
            {...getTriggerProps()}
            variant="opacity"
            size="small"
            withDropdownIcon
            disabled={disabled}
            aria-invalid={isInvalid ? 'true' : undefined}
            className={cx(styles.dropdownTrigger, styles.typeTrigger, {
              [styles.typeTriggerInvalid]: isInvalid,
            })}
          >
            {label}
          </Button>
          <Menu.Content {...getContentProps()} placement="bottom-start" minWidth="160px">
            <Menu.Segment>
              {options.map((option) => (
                <Menu.ActionItem
                  key={option}
                  onClick={() => onSelect(option)}
                  withRightAccessory={
                    metricType === option ? <UIIcon src={CheckmarkLine} /> : undefined
                  }
                >
                  {getMetricTypeLabel(option)}
                </Menu.ActionItem>
              ))}
            </Menu.Segment>
          </Menu.Content>
        </>
      )}
    </Menu.Wrapper>
  );
};

const OutcomeTypeLabel = () => {
  return (
    <Text size="xsmall" color="standard" className={styles.outcomeTypeText} isBold>
      {t('milestone').toString()}
    </Text>
  );
};

const MetricVsPlanMenu = ({
  status,
  onSelect,
  showLabel = false,
  disabled = false,
}: {
  status: string | null;
  onSelect: (status: WorkstreamMetricStatusOption['value']) => void;
  showLabel?: boolean;
  disabled?: boolean;
}) => {
  const selectedStatus = getWorkstreamMetricStatusOption(status);

  return (
    <Menu.Wrapper shouldPropagateClickOutsideMouseEvents>
      {({getContentProps, getTriggerProps}) => (
        <>
          <button
            type="button"
            {...getTriggerProps()}
            className={cx(
              metricStatusStyles.metricStatusTag,
              metricStatusStyles.metricStatusButton,
              !showLabel && metricStatusStyles.metricStatusTagIconOnly,
              statusControlStyles.interactiveStatusTrigger
            )}
            disabled={disabled}
            aria-label={t(selectedStatus.labelKey).toString()}
            title={t(selectedStatus.labelKey).toString()}
          >
            <WorkstreamMetricStatusTagContent
              labelKey={selectedStatus.labelKey}
              iconColor={selectedStatus.iconColor}
              iconSrc={selectedStatus.iconSrc}
              showLabel={showLabel}
            />
          </button>
          <WorkstreamMetricStatusMenuContent
            getContentProps={getContentProps}
            includeActionAriaLabel
            onSelect={onSelect}
            selectedStatus={selectedStatus}
            showLabel
          />
        </>
      )}
    </Menu.Wrapper>
  );
};

const DeleteDraftModal = ({
  row,
  open,
  onClose,
  onDelete,
  isPending,
  draftPolicy,
  workstreamId,
}: {
  row: ManageOutcomeDraftRow | null;
  open: boolean;
  onClose: () => void;
  onDelete: (reason: string) => Promise<void>;
  isPending?: boolean;
  draftPolicy: WorkstreamDraftPolicy | undefined;
  workstreamId: number;
}) => {
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) {
      setReason('');
    }
  }, [open, row?.draftId]);

  if (!row) return null;

  const requiresDeleteReason =
    !row.isNew &&
    Boolean(row.originalMetric) &&
    !isMetricDraftReasonWaived(
      draftPolicy,
      row.originalMetric ? getMetricCreatedAtForDraftPolicy(row.originalMetric, workstreamId) : null
    );

  return (
    <Modal
      open={open}
      onRequestClose={onClose}
      withCloseButton={t('close')}
      width="small"
      isCentered
    >
      <Modal.Header>
        <Modal.Title>
          {t('manage_outcomes_delete_title', {type: getRowLabel(row).toLowerCase()}).toString()}
        </Modal.Title>
        <Text size="medium">
          {row.titleText.trim().length > 0
            ? row.titleText
            : t('manage_outcomes_unnamed_delete_body', {
                type: getRowLabel(row).toLowerCase(),
              }).toString()}
        </Text>
      </Modal.Header>
      <Modal.Body>
        <Stack gap="12">
          <TextInput
            isTransparent
            value={reason}
            onChange={(event) => setReason(event.currentTarget.value)}
            placeholder={t('manage_outcomes_reason_for_deletion').toString()}
            autoFocus
          />
        </Stack>
      </Modal.Body>
      <Modal.Footer>
        <CancelButton onClick={onClose} disabled={isPending}>
          {t('cancel')}
        </CancelButton>
        <Button
          variant="primary"
          onClick={() => void onDelete(reason)}
          isLoading={isPending}
          disabled={requiresDeleteReason && !reason.trim()}
        >
          {t('delete')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

let nextManagedTriggerHostId = 1;
let managedTriggerVersion = 0;
let managedTriggerState: ManagedTriggerState = {
  open: false,
  scope: null,
  initialTab: 'metrics',
};
const managedTriggerHosts = new Map<ManagedTriggerScope, Set<number>>();
const managedTriggerActiveHost = new Map<ManagedTriggerScope, number>();
const managedTriggerHostWorkstreamId = new Map<number, number>();
const managedTriggerListeners = new Set<() => void>();

const emitManagedTriggerChange = () => {
  managedTriggerVersion += 1;
  managedTriggerListeners.forEach((listener) => listener());
};

const subscribeManagedTrigger = (listener: () => void) => {
  managedTriggerListeners.add(listener);
  return () => managedTriggerListeners.delete(listener);
};

const getManagedTriggerSnapshot = () => managedTriggerVersion;

const registerManagedTriggerHost = (
  scope: ManagedTriggerScope,
  hostId: number,
  workstreamId: number
) => {
  managedTriggerHostWorkstreamId.set(hostId, workstreamId);
  const hosts = managedTriggerHosts.get(scope) ?? new Set<number>();
  hosts.add(hostId);
  managedTriggerHosts.set(scope, hosts);
  const activeHostId = managedTriggerActiveHost.get(scope);
  if (!activeHostId) {
    managedTriggerActiveHost.set(scope, hostId);
  } else if (
    managedTriggerState.open &&
    managedTriggerState.scope === scope &&
    managedTriggerState.workstreamId === workstreamId &&
    managedTriggerHostWorkstreamId.get(activeHostId) !== workstreamId
  ) {
    managedTriggerActiveHost.set(scope, hostId);
  }
  emitManagedTriggerChange();
};

const unregisterManagedTriggerHost = (scope: ManagedTriggerScope, hostId: number) => {
  const hosts = managedTriggerHosts.get(scope);
  if (!hosts) return;

  managedTriggerHostWorkstreamId.delete(hostId);
  const wasActiveHost = managedTriggerActiveHost.get(scope) === hostId;
  hosts.delete(hostId);
  if (hosts.size === 0) {
    managedTriggerHosts.delete(scope);
    managedTriggerActiveHost.delete(scope);
    if (managedTriggerState.scope === scope) {
      managedTriggerState = {
        ...managedTriggerState,
        open: false,
        scope: null,
        workstreamId: undefined,
      };
    }
    emitManagedTriggerChange();
    return;
  }

  if (wasActiveHost) {
    if (managedTriggerState.open && managedTriggerState.scope === scope) {
      const matchingHostId = [...hosts].find(
        (candidateHostId) =>
          managedTriggerHostWorkstreamId.get(candidateHostId) === managedTriggerState.workstreamId
      );
      if (matchingHostId) {
        managedTriggerActiveHost.set(scope, matchingHostId);
      } else {
        // Keep the modal scoped to its originating workstream; do not retarget to another host.
        managedTriggerActiveHost.delete(scope);
      }
    } else {
      managedTriggerActiveHost.set(scope, hosts.values().next().value as number);
    }
  }
  emitManagedTriggerChange();
};

const openManagedTriggerModal = (
  scope: ManagedTriggerScope,
  hostId: number,
  props: Pick<
    ManageOutcomesTriggerProps,
    'initialTab' | 'focusMetricId' | 'workstreamOwner' | 'metricPeriodOptions' | 'workstreamId'
  >
) => {
  managedTriggerActiveHost.set(scope, hostId);
  managedTriggerState = {
    open: true,
    scope,
    initialTab: props.initialTab,
    workstreamId: props.workstreamId,
    focusMetricId: props.focusMetricId,
    workstreamOwner: props.workstreamOwner,
    metricPeriodOptions: props.metricPeriodOptions,
  };
  emitManagedTriggerChange();
};

const closeManagedTriggerModal = () => {
  managedTriggerState = {...managedTriggerState, open: false, scope: null, workstreamId: undefined};
  emitManagedTriggerChange();
};

const updateManagedTriggerTab = (scope: ManagedTriggerScope, tab: ManageOutcomesTab) => {
  if (managedTriggerState.scope !== scope || managedTriggerState.initialTab === tab) {
    return;
  }

  managedTriggerState = {...managedTriggerState, initialTab: tab};
};

export const WorkstreamManageOutcomesModal = ({
  open,
  onClose,
  workstreamId,
  workstreamName,
  metrics,
  jiraOutcomes = [],
  workstreamOwner,
  initialTab,
  autoAddRowOnOpen = false,
  onActiveTabChange,
  focusMetricId,
  metricPeriodOptions,
  isPending = false,
  onSaveMetric,
  onAttachMetric,
  onDeleteMetric,
  loadElevatableJiraOutcomes,
  onElevateJiraOutcome,
  onDetachJiraOutcome,
  hiddenMnpiMetricsCount = 0,
}: ManageOutcomesModalProps) => {
  const pulseUser = useAtomValue(pulseUserAtom);
  const {isGtmPilotChangesEnabled} = useAtomValue(growthbookCacheAtom);
  const {rawHealthScore, shouldShowHealthScore} = useWorkstreamHealthScore({
    workstreamId,
    checksType: 'default',
  });
  const shouldEnforceMetricRestrictions = isCorePillarLevel(
    rawHealthScore?.workstream_pillar_metadata?.pillar_level
  );
  const {data: draftPolicy} = useWorkstreamDraftPolicy({enabled: Boolean(pulseUser)});
  const [activeTab, setActiveTab] = useState<VisibleManageOutcomesTab>(
    normalizeManageTab(initialTab)
  );
  const [draftRows, setDraftRows] = useState<ManageOutcomeDraftRow[]>([]);
  const {
    clearPreview: clearOutcomesValidationPreview,
    generatePreview: generateOutcomesValidationPreview,
    getPreview: getOutcomesValidationPreview,
    hasPreviewOrGenerating: hasOutcomesValidationPreviewOrGenerating,
    isGeneratingPreview: isGeneratingOutcomesValidationPreview,
    resetPreviews: resetOutcomesValidationPreviews,
    savePreviewToMetric: saveOutcomesValidationPreviewToMetric,
  } = useMetricOutcomesValidationPreviews({
    enabled: Boolean(shouldShowHealthScore),
  });
  const [selectedMetricsYear, setSelectedMetricsYear] = useState(CURRENT_YEAR);
  const [maxContentHeight, setMaxContentHeight] = useState(getMaxModalContentHeight);
  const handleModalRequestClose = () => {
    if (isSaving || isPending) {
      return;
    }

    if (
      unsavedChangesCount > 0 &&
      !window.confirm(t('unsaved_changes_leave_confirmation').toString())
    ) {
      return;
    }

    resetOutcomesValidationPreviews();
    onClose();
  };
  const [measuredTabHeights, setMeasuredTabHeights] = useState<
    Partial<Record<VisibleManageOutcomesTab, number>>
  >({});
  const [isMeasuringTabHeights, setIsMeasuringTabHeights] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ManageOutcomeDraftRow | null>(null);
  const [deliverableUrlAutoFocusDraftId, setDeliverableUrlAutoFocusDraftId] = useState<
    string | null
  >(null);
  const [deliverableLinkEditDraftIds, setDeliverableLinkEditDraftIds] = useState<string[]>([]);
  const [pendingJiraElevations, setPendingJiraElevations] = useState<JiraObject[]>([]);
  const [linkableMetricSearchTitle, setLinkableMetricSearchTitle] = useState('');
  const {
    elevatableJiraOutcomes,
    isLoadingElevatableJiraOutcomes,
    isLoadingLinkableMetrics,
    linkableMetrics,
    retryFailedLoads,
    setElevatableJiraOutcomes,
  } = useWorkstreamManageOutcomesPickerData({
    open,
    workstreamId,
    loadElevatableJiraOutcomes,
  });
  const [focusedNameDraftId, setFocusedNameDraftId] = useState<string | null>(null);
  const [focusedBaselineDraftId, setFocusedBaselineDraftId] = useState<string | null>(null);
  const [focusedCurrentDraftId, setFocusedCurrentDraftId] = useState<string | null>(null);
  const [focusedMetricForecastCell, setFocusedMetricForecastCell] = useState<string | null>(null);
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const nameFieldRefs = useRef<Record<string, HTMLElement>>({});
  const linkableMetricSearchRef = useRef<HTMLElement | null>(null);
  const pendingNameFocusDraftIdRef = useRef<string | null>(null);
  const measurementRefs = useRef<Record<VisibleManageOutcomesTab, HTMLDivElement | null>>({
    metrics: null,
    outcomes: null,
  });
  const tabsRef = useRef<HTMLDivElement | null>(null);
  const wasOpenRef = useRef(false);
  const setNameFieldRef = useCallback((draftId: string, node: HTMLElement | null) => {
    if (node) {
      nameFieldRefs.current[draftId] = node;
    } else {
      delete nameFieldRefs.current[draftId];
    }
  }, []);
  const focusNameField = useCallback((draftId: string) => {
    const field = nameFieldRefs.current[draftId];
    const target = field?.matches('input, textarea')
      ? field
      : field?.querySelector<HTMLElement>('input, textarea');

    if (!target) {
      return false;
    }

    target.focus();
    if ('setSelectionRange' in target && 'value' in target) {
      const input = target as HTMLInputElement | HTMLTextAreaElement;
      const end = input.value.length;
      input.setSelectionRange(end, end);
    }
    return true;
  }, []);

  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      return;
    }

    if (wasOpenRef.current) {
      return;
    }

    wasOpenRef.current = true;
    setMeasuredTabHeights({});
    setIsMeasuringTabHeights(true);
    const nextRows = getFullWorkstreamMetrics(metrics)
      .filter(
        (metric): metric is WorkstreamMetric =>
          metric.type === 'metric' || metric.type === 'milestone' || metric.type === 'deliverable'
      )
      .sort((a, b) => sortWorkstreamMetrics(a, b, workstreamId))
      .map((metric) => buildDraftRow(metric, workstreamId, metric.id === focusMetricId));
    const normalizedInitialTab = normalizeManageTab(initialTab);
    let nextFocusedNameDraftId: string | null = null;
    if (autoAddRowOnOpen && !nextRows.some((row) => getDraftTab(row) === normalizedInitialTab)) {
      const type = initialTab === 'metrics' ? 'metric' : DEFAULT_OUTCOME_DRAFT_TYPE;
      const nextOrder = Math.max(
        0,
        ...nextRows.filter((row) => row.type === type).map((row) => row.order)
      );
      const nextRow = {
        ...buildEmptyDraftRow(type, nextOrder + 1, workstreamOwner ?? null),
        autoAddedOwnerKey: workstreamOwner?.user_id ?? workstreamOwner?.ldap ?? null,
      };
      nextRows.push(nextRow);
      nextFocusedNameDraftId = nextRow.draftId;
    }
    setDraftRows(nextRows);
    setActiveTab(normalizedInitialTab);
    pendingNameFocusDraftIdRef.current = nextFocusedNameDraftId;
    setDeliverableUrlAutoFocusDraftId(null);
    setDeliverableLinkEditDraftIds([]);
    resetOutcomesValidationPreviews();
    setPendingJiraElevations([]);
    setLinkableMetricSearchTitle('');
    setFocusedNameDraftId(nextFocusedNameDraftId);
    const nextMetricsYears = buildMetricsYears(nextRows, metricPeriodOptions);
    const nextMetricsYear = nextMetricsYears.includes(CURRENT_YEAR)
      ? CURRENT_YEAR
      : nextMetricsYears[0];
    if (typeof nextMetricsYear === 'number') {
      setSelectedMetricsYear(nextMetricsYear);
    }
  }, [
    autoAddRowOnOpen,
    focusMetricId,
    initialTab,
    metricPeriodOptions,
    metrics,
    open,
    resetOutcomesValidationPreviews,
    workstreamId,
    workstreamOwner,
  ]);

  useEffect(() => {
    if (!open) return;
    onActiveTabChange?.(activeTab);
  }, [activeTab, onActiveTabChange, open]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => setMaxContentHeight(getMaxModalContentHeight());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const metricsYears = useMemo(
    () => buildMetricsYears(draftRows, metricPeriodOptions),
    [draftRows, metricPeriodOptions]
  );
  const readOnlyJiraRows = useMemo(() => {
    const existingIds = new Set(jiraOutcomes.map((item) => item.id));
    return [
      ...jiraOutcomes.map((item) => buildReadOnlyJiraRow(item)),
      ...pendingJiraElevations
        .filter((item) => !existingIds.has(item.id))
        .map((item) => buildReadOnlyJiraRow(item, true)),
    ];
  }, [jiraOutcomes, pendingJiraElevations]);

  useEffect(() => {
    if (!open) return;
    setSelectedMetricsYear((currentYear) => {
      if (metricsYears.includes(currentYear)) {
        return currentYear;
      }

      const nextYear = metricsYears.includes(CURRENT_YEAR) ? CURRENT_YEAR : metricsYears[0];
      return typeof nextYear === 'number' ? nextYear : currentYear;
    });
  }, [metricsYears, open]);

  useEffect(() => {
    if (!open || focusMetricId == null) return;
    const matchingRow = draftRows.find((row) => row.metricId === focusMetricId);
    if (!matchingRow) return;
    const rowNode = rowRefs.current[matchingRow.draftId];
    rowNode?.scrollIntoView({block: 'center'});
  }, [draftRows, focusMetricId, open]);

  useLayoutEffect(() => {
    if (!open) return;

    const draftId = pendingNameFocusDraftIdRef.current;
    if (!draftId) return;

    // Dig restores focus to the typeahead trigger after its keyboard selection effect finishes.
    // Defer the name-field focus so the newly created row wins that focus handoff.
    const focusTimeout = window.setTimeout(() => {
      if (pendingNameFocusDraftIdRef.current !== draftId) return;
      if (focusNameField(draftId)) {
        pendingNameFocusDraftIdRef.current = null;
      }
    }, 0);

    return () => window.clearTimeout(focusTimeout);
  }, [activeTab, draftRows, focusNameField, open]);

  const counts = useMemo(() => getTabCounts(draftRows), [draftRows]);
  const tabCounts = useMemo(
    () => ({
      ...counts,
      outcomes: counts.outcomes + readOnlyJiraRows.length,
    }),
    [counts, readOnlyJiraRows.length]
  );
  const dirtyRows = useMemo(
    () => draftRows.filter((row) => isDirty(row, workstreamId)),
    [draftRows, workstreamId]
  );
  const unsavedChangesCount = dirtyRows.length + pendingJiraElevations.length;
  const orderedMetricRows = useMemo(
    () => orderMetricDraftRows(draftRows.filter((row) => row.type === 'metric')),
    [draftRows]
  );
  const metricSortableIds = useMemo(
    () => orderedMetricRows.map((row) => row.draftId),
    [orderedMetricRows]
  );
  const reorderSensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const applyMetricDraftOrder = useCallback((orderedDraftIds: string[]) => {
    const nextOrderByDraftId = new Map(
      orderedDraftIds.map((draftId, index) => [draftId, index + 1])
    );
    setDraftRows((current) =>
      current.map((row) =>
        row.type === 'metric' && nextOrderByDraftId.has(row.draftId)
          ? {...row, order: nextOrderByDraftId.get(row.draftId)!}
          : row
      )
    );
  }, []);
  const moveMetricDraftRow = useCallback((draftId: string, offset: -1 | 1) => {
    setDraftRows((current) => {
      const orderedRows = orderMetricDraftRows(current.filter((row) => row.type === 'metric'));
      const fromIndex = orderedRows.findIndex((row) => row.draftId === draftId);
      const toIndex = fromIndex + offset;
      if (fromIndex < 0 || toIndex < 0 || toIndex >= orderedRows.length) {
        return current;
      }
      if (!canReorderMetricDraftRows(orderedRows[fromIndex], orderedRows[toIndex])) {
        return current;
      }

      const reorderedIds = arrayMove(orderedRows, fromIndex, toIndex).map((row) => row.draftId);
      const nextOrderByDraftId = new Map(
        reorderedIds.map((candidateDraftId, index) => [candidateDraftId, index + 1])
      );
      return current.map((row) =>
        row.type === 'metric' && nextOrderByDraftId.has(row.draftId)
          ? {...row, order: nextOrderByDraftId.get(row.draftId)!}
          : row
      );
    });
  }, []);
  const handleMetricDragEnd = useCallback(
    ({active, over}: DragEndEvent) => {
      if (!over || active.id === over.id) {
        return;
      }

      const oldIndex = metricSortableIds.indexOf(String(active.id));
      const newIndex = metricSortableIds.indexOf(String(over.id));
      if (oldIndex < 0 || newIndex < 0) {
        return;
      }
      if (!canReorderMetricDraftRows(orderedMetricRows[oldIndex], orderedMetricRows[newIndex])) {
        return;
      }

      applyMetricDraftOrder(arrayMove(metricSortableIds, oldIndex, newIndex));
    },
    [applyMetricDraftOrder, metricSortableIds, orderedMetricRows]
  );
  const showLinkedMetricIndicatorColumn = useMemo(
    () => orderedMetricRows.some((row) => shouldShowLinkedMetricIndicator(row)),
    [orderedMetricRows]
  );
  const orderedOutcomeRows = useMemo(
    () =>
      orderOutcomeDraftRows(
        draftRows.filter((row) => row.type === 'milestone' || row.type === 'deliverable')
      ),
    [draftRows]
  );
  const showOutcomeLinkedMetricIndicatorColumn = useMemo(
    () =>
      orderedOutcomeRows.some(shouldShowLinkedMetricIndicator) ||
      readOnlyJiraRows.some((row) => isLinkedJiraOutcome(row, workstreamId)),
    [orderedOutcomeRows, readOnlyJiraRows, workstreamId]
  );
  const orderedOutcomeItems = useMemo(
    () =>
      [
        ...orderedOutcomeRows.map((row) => ({kind: 'draft' as const, row})),
        ...readOnlyJiraRows.map((row) => ({kind: 'jira' as const, row})),
      ].sort((a, b) => {
        const aPrimaryRank =
          a.kind === 'draft' && isStarrableOutcome(a.row) && a.row.isPrimary ? 0 : 1;
        const bPrimaryRank =
          b.kind === 'draft' && isStarrableOutcome(b.row) && b.row.isPrimary ? 0 : 1;

        if (aPrimaryRank !== bPrimaryRank) {
          return aPrimaryRank - bPrimaryRank;
        }

        const aTime = a.row.completeDate ? new Date(a.row.completeDate).getTime() : Infinity;
        const bTime = b.row.completeDate ? new Date(b.row.completeDate).getTime() : Infinity;

        if (aTime !== bTime) {
          return aTime - bTime;
        }

        if (a.kind !== b.kind) {
          return a.kind === 'draft' ? -1 : 1;
        }

        if (a.kind === 'draft' && b.kind === 'draft' && a.row.order !== b.row.order) {
          return a.row.order - b.row.order;
        }

        const aKey = a.kind === 'draft' ? a.row.draftId : a.row.key;
        const bKey = b.kind === 'draft' ? b.row.draftId : b.row.key;
        return aKey.localeCompare(bKey);
      }),
    [orderedOutcomeRows, readOnlyJiraRows]
  );
  const tabValidationCounts = useMemo(
    () =>
      draftRows.reduce<Record<VisibleManageOutcomesTab, number>>(
        (accumulator, row) => ({
          ...accumulator,
          [getDraftTab(row)]:
            accumulator[getDraftTab(row)] +
            (getRowValidationIssue(
              row,
              workstreamId,
              workstreamOwner,
              draftPolicy,
              draftRows,
              shouldEnforceMetricRestrictions
            )
              ? 1
              : 0),
        }),
        {
          metrics: 0,
          outcomes: 0,
        }
      ),
    [draftPolicy, draftRows, shouldEnforceMetricRestrictions, workstreamId, workstreamOwner]
  );
  const footerValidationSummary = useMemo(
    () =>
      getFooterValidationSummary(
        draftRows,
        workstreamId,
        workstreamOwner,
        draftPolicy,
        shouldEnforceMetricRestrictions
      ),
    [draftRows, draftPolicy, shouldEnforceMetricRestrictions, workstreamId, workstreamOwner]
  );

  useLayoutEffect(() => {
    if (!open) return;

    setIsMeasuringTabHeights(true);
  }, [draftRows, open, selectedMetricsYear]);

  useLayoutEffect(() => {
    if (!open || !isMeasuringTabHeights) return;

    const nextHeights = VISIBLE_TABS.reduce<Partial<Record<VisibleManageOutcomesTab, number>>>(
      (accumulator, tab) => {
        const node = measurementRefs.current[tab];
        if (!node) {
          return accumulator;
        }

        return {
          ...accumulator,
          [tab]: Math.max(MIN_MODAL_CONTENT_HEIGHT, node.scrollHeight),
        };
      },
      {}
    );

    setMeasuredTabHeights((current) => {
      const hasChanged = VISIBLE_TABS.some((tab) => current[tab] !== nextHeights[tab]);
      return hasChanged ? nextHeights : current;
    });
    setIsMeasuringTabHeights(false);
  }, [isMeasuringTabHeights, open]);

  const modalContentHeight = Math.min(
    maxContentHeight,
    Math.max(MIN_MODAL_CONTENT_HEIGHT, ...Object.values(measuredTabHeights))
  );
  const modalBodyHeight =
    modalContentHeight + (tabsRef.current?.getBoundingClientRect().height ?? 0) + 16;

  const setRow = (
    draftId: string,
    updater: (row: ManageOutcomeDraftRow) => ManageOutcomeDraftRow
  ) =>
    setDraftRows((current) => current.map((row) => (row.draftId === draftId ? updater(row) : row)));

  const regenerateWorkstreamOutcomesValidation = useCallback(
    (row?: ManageOutcomeDraftRow) => {
      if (!shouldShowHealthScore || workstreamId <= 0) return;

      if (!row || !shouldShowOutcomesValidation(row)) return;
      if (isGeneratingOutcomesValidationPreview(row)) return;

      const shouldRegenerateForNameChange = row.originalMetric
        ? hasTextChangedAfterInitialSelection(
            getPlainTextTitle(row.originalMetric.title),
            getPlainTextTitle(row.titleText)
          )
        : row.isNew && getPlainTextTitle(row.titleText).length > 0;
      if (shouldRegenerateForNameChange) {
        void generateOutcomesValidationPreview(row);
      }
    },
    [
      generateOutcomesValidationPreview,
      isGeneratingOutcomesValidationPreview,
      shouldShowHealthScore,
      workstreamId,
    ]
  );

  const addRow = (tab: VisibleManageOutcomesTab) => {
    const type = tab === 'metrics' ? 'metric' : DEFAULT_OUTCOME_DRAFT_TYPE;
    const nextDraftId = getTempDraftKey(type, 0);
    const nextTitleText = linkableMetricSearchTitle.trim();

    pendingNameFocusDraftIdRef.current = nextDraftId;
    setFocusedNameDraftId(nextDraftId);

    setDraftRows((current) => {
      const sameType = current.filter((row) => row.type === type);
      const nextOrder = Math.max(0, ...sameType.map((row) => row.order)) + 1;
      const nextRow = buildEmptyDraftRow(type, nextOrder, workstreamOwner ?? null, nextDraftId);
      return [...current, nextTitleText ? {...nextRow, titleText: nextTitleText} : nextRow];
    });
    if (nextTitleText) {
      setLinkableMetricSearchTitle('');
    }
  };

  const clearLinkableSearch = () => {
    setLinkableMetricSearchTitle('');

    const field = linkableMetricSearchRef.current;
    const target = field?.matches('input, textarea')
      ? field
      : field?.querySelector<HTMLElement>('input, textarea');
    target?.blur();
  };

  const addSelectedPickerOption = (option: ManageOutcomePickerOption) => {
    if (option.kind === 'jira') {
      setPendingJiraElevations((current) =>
        current.some((item) => item.id === option.jiraObject.id)
          ? current
          : [...current, option.jiraObject]
      );
      setElevatableJiraOutcomes((current) =>
        current.filter((item) => item.id !== option.jiraObject.id)
      );
      clearLinkableSearch();
      return;
    }

    setDraftRows((current) => {
      const metric = option.metric;
      if (current.some((row) => row.metricId === metric.id)) {
        return current;
      }

      const sameType = current.filter((row) => row.type === metric.type);
      const nextOrder = Math.max(0, ...sameType.map((row) => row.order)) + 1;
      return [
        ...current,
        {
          ...buildSelectedExistingDraftRow(metric, workstreamId, nextOrder),
          isLinkedMetric: true,
        },
      ];
    });
    clearLinkableSearch();
  };

  const getFilteredPickerOptions = useCallback(
    (tab: VisibleManageOutcomesTab, draftId: string | null, search: string) => {
      const existingMetricIds = new Set(
        draftRows
          .filter((row) => !draftId || row.draftId !== draftId)
          .map((row) => row.metricId)
          .filter((metricId): metricId is number => metricId != null)
      );
      const existingJiraObjectIds = new Set(readOnlyJiraRows.map((row) => row.id));
      const normalizedSearch = search.trim().toLowerCase();
      const workstreamOptions = (
        onAttachMetric && tab === 'metrics'
          ? linkableMetrics.filter((metric) => metric.type === 'metric')
          : onAttachMetric
            ? linkableMetrics.filter(
                (metric) => metric.type !== 'metric' && metric.type !== 'deliverable'
              )
            : []
      )
        .filter((metric) => !existingMetricIds.has(metric.id))
        .map(buildWorkstreamMetricPickerOption);
      const jiraOptions =
        tab === 'outcomes'
          ? elevatableJiraOutcomes
              .filter((item) => !existingJiraObjectIds.has(item.id))
              .map(buildJiraPickerOption)
          : [];

      return [...workstreamOptions, ...jiraOptions].filter((option) => {
        if (!normalizedSearch) {
          return true;
        }

        const haystack = [option.title, option.subtitle].filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(normalizedSearch);
      });
    },
    [draftRows, elevatableJiraOutcomes, linkableMetrics, onAttachMetric, readOnlyJiraRows]
  );

  const canShowLinkableMetricSearch = true;

  const saveDisabled = useMemo(() => {
    return dirtyRows.some(
      (row) =>
        getRowValidationIssue(
          row,
          workstreamId,
          workstreamOwner,
          draftPolicy,
          draftRows,
          shouldEnforceMetricRestrictions
        ) !== null
    );
  }, [
    dirtyRows,
    draftPolicy,
    draftRows,
    shouldEnforceMetricRestrictions,
    workstreamId,
    workstreamOwner,
  ]);

  const persistSavedOutcomesValidationPreview = useCallback(
    async (row: ManageOutcomeDraftRow, savedMetric?: WorkstreamMetric | void) => {
      const savedMetricId = savedMetric?.id ?? row.metricId;
      if (
        !shouldShowHealthScore ||
        workstreamId <= 0 ||
        !shouldShowOutcomesValidation(row) ||
        !hasOutcomesValidationPreviewOrGenerating(row) ||
        savedMetricId == null
      ) {
        return;
      }

      await saveOutcomesValidationPreviewToMetric(row, savedMetricId).catch(() => undefined);
    },
    [
      saveOutcomesValidationPreviewToMetric,
      hasOutcomesValidationPreviewOrGenerating,
      shouldShowHealthScore,
      workstreamId,
    ]
  );

  const handleSave = async () => {
    if (!unsavedChangesCount) {
      onClose();
      return;
    }

    if (
      dirtyRows.some(
        (row) =>
          getRowValidationIssue(
            row,
            workstreamId,
            workstreamOwner,
            draftPolicy,
            draftRows,
            shouldEnforceMetricRestrictions
          ) !== null
      )
    ) {
      return;
    }

    // Metric status is edited as part of the batched modal draft, so we log the
    // change (attributed to the modal) at persist time for rows whose status
    // actually changed vs. the original metric.
    const logMetricStatusChange = (row: ManageOutcomeDraftRow) => {
      if (row.type !== 'metric' || !row.originalMetric || row.metricId == null) return;
      const previousStatus = row.originalMetric.status ?? 'no_status';
      const nextStatus = normalizeMetricStatus(row);
      if (previousStatus === nextStatus) return;
      logWorkstreamMetricStatusChanged('edit_modal', {
        workstream_id: workstreamId,
        metric_id: row.metricId,
        status: nextStatus,
        previous_status: previousStatus,
      });
    };

    setIsSaving(true);
    try {
      for (const row of dirtyRows) {
        if (row.isPendingAttach && row.metricId != null) {
          const attachedMetric =
            (await onAttachMetric?.(
              {
                metric_id: row.metricId,
                order: row.order,
                is_primary: isNorthStarMetric(row),
              },
              undefined
            )) ?? undefined;
          if (hasRowContentChanges(row, workstreamId)) {
            logMetricStatusChange(row);
            await onSaveMetric(
              row.metricId,
              buildMetricPayload(row, workstreamId, isGtmPilotChangesEnabled),
              row.reason.trim() || undefined,
              attachedMetric
            );
          }
          continue;
        }

        logMetricStatusChange(row);
        const savedMetric = await onSaveMetric(
          row.metricId ?? -1,
          buildMetricPayload(row, workstreamId, isGtmPilotChangesEnabled),
          row.reason.trim() || undefined,
          row.originalMetric ?? undefined
        );
        await persistSavedOutcomesValidationPreview(row, savedMetric);
      }
      for (const jiraOutcome of pendingJiraElevations) {
        await onElevateJiraOutcome?.(jiraOutcome.id);
      }
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (reason: string) => {
    if (!deleteTarget) return;

    if (
      deleteTarget.isNew ||
      deleteTarget.isPendingAttach ||
      deleteTarget.metricId == null ||
      !deleteTarget.originalMetric
    ) {
      clearOutcomesValidationPreview(deleteTarget);
      setDraftRows((current) => current.filter((row) => row.draftId !== deleteTarget.draftId));
      setDeleteTarget(null);
      return;
    }

    if (!onDeleteMetric) return;
    if (deleteTarget.originalMetric) {
      await onDeleteMetric(deleteTarget.originalMetric, reason);
    }
    clearOutcomesValidationPreview(deleteTarget);
    setDraftRows((current) => current.filter((row) => row.draftId !== deleteTarget.draftId));
    setDeleteTarget(null);
  };

  const handleDeleteClick = (row: ManageOutcomeDraftRow) => {
    if (row.isNew || row.isPendingAttach || row.metricId == null || !row.originalMetric) {
      clearOutcomesValidationPreview(row);
      setDraftRows((current) => current.filter((candidate) => candidate.draftId !== row.draftId));
      return;
    }

    if (shouldShowLinkedMetricIndicator(row)) {
      if (!onDeleteMetric) return;
      // For linked metrics, we can immediately unlink them without showing confirmation since
      // user can easily just relink them
      const originalMetric = row.originalMetric;
      if (!originalMetric) return;
      void (async () => {
        await onDeleteMetric(originalMetric, '');
        clearOutcomesValidationPreview(row);
        setDraftRows((current) => current.filter((candidate) => candidate.draftId !== row.draftId));
      })();
      return;
    }

    setDeleteTarget(row);
  };

  const renderMetricForecastGrid = (row: ManageOutcomeDraftRow, year: number, disabled = false) => {
    const hasMissingRequiredBudgetError = hasMetricBudgetRestrictionError(
      row,
      workstreamId,
      shouldEnforceMetricRestrictions
    );
    const canShowInvalidUnderline = canShowInvalidMetricValueUnderline(
      row,
      workstreamId,
      shouldEnforceMetricRestrictions
    );

    return (
      <div className={styles.metricForecastGridCell}>
        <MetricForecastGrid
          year={year}
          className={styles.metricForecastGrid}
          rowClassName={styles.metricForecastGridRow}
          labelClassName={styles.metricForecastRowLabel}
          renderCell={({field, period, periodId}) => {
            const forecast = row.metricForecasts[period] ?? {
              period,
              target: '',
              budget: '',
              actual: '',
              forecast_date: null,
            };
            const cellKey = `${row.draftId}:${period}:${field}`;
            const metricValueInput = (
              <TextInput
                size="small"
                isTransparent
                wrapperProps={{
                  className: cx({
                    [metricValueValidationStyles.invalidMetricValueInput]:
                      canShowInvalidUnderline &&
                      focusedMetricForecastCell !== cellKey &&
                      hasInvalidMetricForecastValue(forecast[field]),
                  }),
                }}
                className={styles.metricValueInput}
                disabled={
                  disabled || (field === 'actual' && !hasMetricForecastPeriodEnded(year, periodId))
                }
                value={forecast[field] ?? ''}
                onFocus={() => setFocusedMetricForecastCell(cellKey)}
                onBlur={() =>
                  setFocusedMetricForecastCell((current) => (current === cellKey ? null : current))
                }
                onChange={(event) => {
                  const nextValue = event.currentTarget.value;
                  setRow(row.draftId, (current) => ({
                    ...current,
                    metricForecasts: {
                      ...current.metricForecasts,
                      [period]: {
                        ...forecast,
                        [field]: nextValue,
                      },
                    },
                  }));
                }}
                placeholder="-"
                aria-label={`${field} ${period}`}
              />
            );

            const isRequiredBudgetCell = field === 'budget' && isRequiredBudgetPeriod(period);
            const showBudgetRestrictionCell =
              isDirty(row, workstreamId) && shouldEnforceMetricRestrictions && isRequiredBudgetCell;
            if (!showBudgetRestrictionCell) {
              return metricValueInput;
            }

            return (
              <RestrictedMetricForecastValueInput
                draftId={row.draftId}
                focusedMetricForecastCell={focusedMetricForecastCell}
                hasMissingRequiredBudgetError={hasMissingRequiredBudgetError}
                periodId={periodId}
                year={year}
              >
                {metricValueInput}
              </RestrictedMetricForecastValueInput>
            );
          }}
        />
      </div>
    );
  };

  const renderMetricReorderCell = (
    row: ManageOutcomeDraftRow,
    handleProps?: MetricReorderHandleProps
  ) => {
    const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') {
        return;
      }
      event.preventDefault();
      moveMetricDraftRow(row.draftId, event.key === 'ArrowUp' ? -1 : 1);
    };

    return (
      <div className={styles.reorderCell}>
        {handleProps ? (
          <Tooltip title={t('dashboard_drag_to_move').toString()} placement="top">
            <IconButton
              ref={handleProps.setActivatorNodeRef}
              variant="borderless"
              size="small"
              className={cx(styles.reorderButton, {
                [styles.reorderButtonDragging]: handleProps.isDragging,
              })}
              aria-label={t('dashboard_drag_to_move').toString()}
              {...handleProps.attributes}
              {...(handleProps.listeners ?? {})}
              onKeyDown={onKeyDown}
            >
              <UIIcon src={DragHandleLine} size="small" />
            </IconButton>
          </Tooltip>
        ) : null}
      </div>
    );
  };

  const setRowContainerRef =
    (row: ManageOutcomeDraftRow, attachRowRef: boolean): RefCallback<HTMLDivElement> =>
    (node) => {
      if (attachRowRef) {
        rowRefs.current[row.draftId] = node;
      }
    };

  const renderMetricRow = (
    row: ManageOutcomeDraftRow,
    attachRowRef = true,
    reorderHandleProps?: MetricReorderHandleProps
  ) => {
    const pinDisabledTooltipKey = getPinDisabledTooltipKey(row, draftRows);
    const canPin = canPinMetricRow(row, draftRows);
    const pinTooltipTitle = pinDisabledTooltipKey
      ? t(pinDisabledTooltipKey).toString()
      : t('manage_outcomes_pin_north_star_instruction').toString();
    const rowError = validateRow(
      row,
      workstreamId,
      workstreamOwner,
      draftPolicy,
      draftRows,
      shouldEnforceMetricRestrictions
    );
    const hasNameError = hasMissingNameError(row, workstreamId);
    const showNameError =
      hasNameError &&
      focusedNameDraftId !== row.draftId &&
      pendingNameFocusDraftIdRef.current !== row.draftId;
    const hasOwnerError = hasMissingOwnerError(row, workstreamId, workstreamOwner);
    const hasMetricTypeError = hasMissingMetricTypeError(row, workstreamId);
    const enforceMetricRestrictionsForRow = shouldEnforceMetricRestrictionsForRow(
      row,
      workstreamId,
      shouldEnforceMetricRestrictions
    );
    const canShowInvalidUnderline = canShowInvalidMetricValueUnderline(
      row,
      workstreamId,
      shouldEnforceMetricRestrictions
    );
    const isBaselineFocused = focusedBaselineDraftId === row.draftId;
    const isCurrentFocused = focusedCurrentDraftId === row.draftId;
    const hasMissingBaselineError = enforceMetricRestrictionsForRow && !row.baseline.trim();
    const rowRequiresReason = requiresReason(
      row,
      workstreamId,
      workstreamOwner,
      draftPolicy,
      draftRows
    );
    const showMetricDetails = isGtmPilotChangesEnabled && row.type === 'metric';
    const showReasonRow = rowRequiresReason || Boolean(rowError && !hasNameError && !hasOwnerError);
    const isReasonInvalid = hasMissingReasonError(
      row,
      workstreamId,
      workstreamOwner,
      draftPolicy,
      draftRows
    );
    const showOutcomesValidation = shouldShowHealthScore && shouldShowOutcomesValidation(row);

    return (
      <div
        key={row.draftId}
        ref={setRowContainerRef(row, attachRowRef)}
        className={cx({
          [styles.draggingMetricRow]: reorderHandleProps?.isDragging,
        })}
      >
        <div
          className={cx(
            styles.tableRow,
            (showMetricDetails || showReasonRow) && styles.metricRowWithSecondary,
            styles.metricsColumns,
            showLinkedMetricIndicatorColumn && styles.metricsColumnsWithLinkedIndicator
          )}
        >
          {renderMetricReorderCell(row, reorderHandleProps)}
          <div className={styles.pinCell}>
            <OptionalTooltip
              enabled
              title={pinTooltipTitle}
              placement="bottom-start"
              auto
              maxWidth={280}
              wrapperClassName={styles.pinTooltipAnchor}
            >
              <IconButton
                variant="borderless"
                size="small"
                className={cx(styles.pinButton, {[styles.pinButtonDisabled]: !canPin})}
                disabled={!canPin}
                onClick={() =>
                  setRow(row.draftId, (current) => ({...current, isPrimary: !current.isPrimary}))
                }
                aria-label={
                  row.isPrimary
                    ? `${t('unstar').toString()} ${row.titleText || getRowLabel(row).toLowerCase()}`
                    : `${t('star').toString()} ${row.titleText || getRowLabel(row).toLowerCase()}`
                }
              >
                <UIIcon
                  src={row.isPrimary ? StarFill : StarLine}
                  size="small"
                  className={cx(styles.pinIcon, {
                    [styles.pinIconActive]: row.isPrimary,
                    [styles.pinIconDisabled]: !canPin,
                  })}
                />
              </IconButton>
            </OptionalTooltip>
          </div>
          <div className={styles.metricsStatusCell}>
            <MetricVsPlanMenu
              status={normalizeMetricStatus(row)}
              onSelect={(status) => setRow(row.draftId, (current) => ({...current, status}))}
              showLabel
            />
          </div>
          {showLinkedMetricIndicatorColumn && (
            <LinkedWorkstreamIndicatorCell
              showIcon={shouldShowLinkedMetricIndicator(row)}
              itemType="metric"
            />
          )}
          <div className={cx(styles.readOnlyCell, styles.metricTitleWithHealthScore)}>
            {showOutcomesValidation ? (
              <WorkstreamOutcomesValidationChecks
                activeTab={activeTab}
                workstreamId={workstreamId}
                validationMetric={row}
              />
            ) : (
              <Box marginLeft="20" />
            )}
            <Box className={styles.metricNameContent}>
              <ManageOutcomeTitleField
                value={row.titleText}
                isInvalid={showNameError}
                fieldRef={(node) => setNameFieldRef(row.draftId, node)}
                onChange={(value) =>
                  setRow(row.draftId, (current) => ({
                    ...current,
                    titleText: value,
                  }))
                }
                onFocus={() => setFocusedNameDraftId(row.draftId)}
                onBlur={() =>
                  setFocusedNameDraftId((current) => (current === row.draftId ? null : current))
                }
                placeholder={t('manage_outcomes_new_metric_name').toString()}
              />
              {showMetricDetails ? (
                <Stack className={styles.metricDetailsFields} gap="4">
                  <Box className={styles.metricDetailField}>
                    <Text size="small" color="subtle" className={styles.metricDetailLabel}>
                      {t('metric_link').toString()}
                    </Text>
                    <TextInput
                      size="small"
                      value={row.url}
                      onChange={(event) => {
                        const nextValue = event.currentTarget.value;
                        setRow(row.draftId, (current) => ({...current, url: nextValue}));
                      }}
                      placeholder={t('enter_url').toString()}
                      aria-label={`${t('metric_link').toString()} ${row.titleText || row.draftId}`}
                      className={styles.metricDetailInput}
                    />
                    {row.url.trim() ? (
                      <a
                        href={normalizeExternalUrl(row.url.trim())}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={t('open_metric_link_for', {
                          name: row.titleText || getRowLabel(row),
                        }).toString()}
                        className={styles.metricDetailOpenLink}
                        onMouseDown={(event: React.MouseEvent<HTMLAnchorElement>) => {
                          event.stopPropagation();
                        }}
                        onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
                          event.stopPropagation();
                        }}
                      >
                        <UIIcon src={OpenLine} size="small" />
                      </a>
                    ) : null}
                  </Box>
                  <Box className={styles.metricDetailField}>
                    <Text size="small" color="subtle" className={styles.metricDetailLabel}>
                      {t('metric_definition').toString()}
                    </Text>
                    <TextInput
                      size="small"
                      value={row.definition ?? ''}
                      onChange={(event) => {
                        const nextValue = event.currentTarget.value;
                        setRow(row.draftId, (current) => ({...current, definition: nextValue}));
                      }}
                      placeholder={t('enter_metric_definition').toString()}
                      aria-label={`${t('metric_definition').toString()} ${row.titleText || row.draftId}`}
                      className={styles.metricDetailInput}
                    />
                  </Box>
                </Stack>
              ) : null}
            </Box>
          </div>
          <div className={cx(styles.compactFieldCell, styles.metricsDriCell)}>
            <EmployeeSelect
              selection={row.owner ?? workstreamOwner ?? undefined}
              isInvalid={hasOwnerError}
              setSelection={(employee) =>
                setRow(row.draftId, (current) => ({...current, owner: employee ?? null}))
              }
            />
          </div>
          <div className={styles.metricsTypeCell}>
            <MetricTypeMenu
              metricType={row.metricType}
              isMetricNew={row.isNew}
              isInvalid={hasMetricTypeError}
              allowOtherOption={false}
              onSelect={(metricType) =>
                setRow(row.draftId, (current) => ({
                  ...current,
                  metricType,
                  isPrimary:
                    isNorthStarMetricType(metricType) && current.isPrimary
                      ? !hasPrimaryNorthStarMetric(draftRows, current.draftId)
                      : false,
                }))
              }
            />
          </div>
          <TextInput
            size="small"
            isTransparent
            isInvalid={hasMissingBaselineError && !isBaselineFocused}
            value={row.baseline}
            wrapperProps={{
              className: cx(styles.baselineInput, {
                [metricValueValidationStyles.invalidMetricValueInput]:
                  canShowInvalidUnderline &&
                  !isBaselineFocused &&
                  hasInvalidMetricBaselineValue(row.baseline),
              }),
            }}
            className={cx(styles.currentInput, styles.metricValueInput)}
            onFocus={() => setFocusedBaselineDraftId(row.draftId)}
            onBlur={() =>
              setFocusedBaselineDraftId((current) => (current === row.draftId ? null : current))
            }
            onChange={(event) => {
              const nextValue = event.currentTarget.value;
              setRow(row.draftId, (current) => ({...current, baseline: nextValue}));
            }}
            placeholder="-"
            aria-label={`baseline ${row.titleText || row.draftId}`}
          />
          <TextInput
            size="small"
            isTransparent
            value={row.current}
            wrapperProps={{
              className: cx(styles.currentValueInput, {
                [metricValueValidationStyles.invalidMetricValueInput]:
                  canShowInvalidUnderline &&
                  !isCurrentFocused &&
                  hasInvalidMetricCurrentValue(row.current),
              }),
            }}
            className={cx(styles.currentInput, styles.metricValueInput)}
            onFocus={() => setFocusedCurrentDraftId(row.draftId)}
            onBlur={() =>
              setFocusedCurrentDraftId((current) => (current === row.draftId ? null : current))
            }
            onChange={(event) => {
              const nextValue = event.currentTarget.value;
              setRow(row.draftId, (current) => ({...current, current: nextValue}));
            }}
            placeholder="-"
            aria-label={`current ${row.titleText || row.draftId}`}
          />
          {renderMetricForecastGrid(row, selectedMetricsYear)}
          <div className={cx(styles.iconCell, styles.deleteActionCell)}>
            <IconButton
              variant="borderless"
              size="small"
              className={styles.deleteButton}
              onClick={() => handleDeleteClick(row)}
              aria-label={`Delete ${row.titleText || 'metric'}`}
            >
              <UIIcon src={DeleteLine} size="small" />
            </IconButton>
          </div>
        </div>
        {showMetricDetails || showReasonRow ? (
          <div className={styles.metricSecondaryRow}>
            <div />
            <div className={styles.metricSecondaryContent}>
              {rowRequiresReason ? (
                <div className={styles.metricSecondaryInline}>
                  <div
                    className={cx(
                      styles.reasonInput,
                      styles.metricSecondaryInput,
                      isReasonInvalid && styles.metricSecondaryInputInvalid
                    )}
                  >
                    <TextInput
                      size="small"
                      isInvalid={isReasonInvalid}
                      value={row.reason}
                      onChange={(event) => {
                        const nextValue = event.currentTarget.value;
                        setRow(row.draftId, (current) => ({
                          ...current,
                          reason: nextValue,
                        }));
                      }}
                      placeholder={t('manage_outcomes_reason_for_change').toString()}
                    />
                  </div>
                  {rowError && !hasNameError && !hasOwnerError ? (
                    <Text size="small" color="error" className={styles.metricSecondaryError}>
                      {rowError}
                    </Text>
                  ) : null}
                </div>
              ) : null}
              {!rowRequiresReason && rowError && !hasNameError && !hasOwnerError ? (
                <Text size="small" color="error">
                  {rowError}
                </Text>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  const renderOutcomeRow = (row: ManageOutcomeDraftRow, attachRowRef = true) => {
    const rowError = validateRow(
      row,
      workstreamId,
      workstreamOwner,
      draftPolicy,
      draftRows,
      shouldEnforceMetricRestrictions
    );
    const hasNameError = hasMissingNameError(row, workstreamId);
    const showNameError =
      hasNameError &&
      focusedNameDraftId !== row.draftId &&
      pendingNameFocusDraftIdRef.current !== row.draftId;
    const hasOwnerError = hasMissingOwnerError(row, workstreamId, workstreamOwner);
    const showUrlInput =
      getOutcomeStatus({status: row.status}) === 'completed' || row.url.trim().length > 0;
    const terminalOnOpen = wasOutcomeTerminalOnOpen(row);
    const isEditingDeliverableLink = deliverableLinkEditDraftIds.includes(row.draftId);
    const rowRequiresReason = requiresReason(
      row,
      workstreamId,
      workstreamOwner,
      draftPolicy,
      draftRows
    );
    const isReasonInvalid = hasMissingReasonError(
      row,
      workstreamId,
      workstreamOwner,
      draftPolicy,
      draftRows
    );
    const pinDisabledTooltipKey = getPinDisabledTooltipKey(row, draftRows);
    const canPin = canPinMetricRow(row, draftRows);
    const hasDeliverableValue = showUrlInput && row.url.trim().length > 0;
    const hadOutcomeLinkOnOpen = Boolean(row.originalMetric?.url?.trim());
    const showDeliverableLinkActions =
      showUrlInput && terminalOnOpen && hadOutcomeLinkOnOpen && !isEditingDeliverableLink;
    const showDeliverableUrlInput = showUrlInput && !showDeliverableLinkActions;
    const renderDeliverableUrlIconOutside =
      hasDeliverableValue && shouldRenderDeliverableUrlIconOutside(row.url);
    const deliverableUrlError =
      showDeliverableUrlInput && !rowRequiresReason && rowError && !hasNameError && !hasOwnerError
        ? rowError
        : null;
    const showSecondaryRow =
      showUrlInput || rowRequiresReason || Boolean(rowError && !hasNameError && !hasOwnerError);
    const showDeliverableOutcomesValidation =
      row.type === 'deliverable' &&
      !isTerminalOutcomeStatus(getOutcomeStatus({status: row.status}));
    const showOutcomesValidation =
      showDeliverableOutcomesValidation ||
      (shouldShowHealthScore && shouldShowOutcomesValidation(row));

    return (
      <div
        key={row.draftId}
        ref={attachRowRef ? (node) => (rowRefs.current[row.draftId] = node) : undefined}
      >
        <div
          className={cx(
            styles.tableRow,
            showSecondaryRow && styles.outcomeRowWithSecondary,
            styles.outcomesColumns
          )}
        >
          <div className={styles.pinCell}>
            {isStarrableOutcome(row) ? (
              <OptionalTooltip
                enabled={Boolean(pinDisabledTooltipKey)}
                title={pinDisabledTooltipKey ? t(pinDisabledTooltipKey).toString() : undefined}
                placement="right-start"
                auto
                maxWidth={280}
                wrapperClassName={styles.pinTooltipAnchor}
              >
                <IconButton
                  variant="borderless"
                  size="small"
                  className={cx(styles.pinButton, {[styles.pinButtonDisabled]: !canPin})}
                  disabled={!canPin}
                  onClick={() =>
                    setRow(row.draftId, (current) => ({...current, isPrimary: !current.isPrimary}))
                  }
                  aria-label={
                    row.isPrimary
                      ? `${t('unstar').toString()} ${
                          row.titleText || getRowLabel(row).toLowerCase()
                        }`
                      : `${t('star').toString()} ${row.titleText || getRowLabel(row).toLowerCase()}`
                  }
                >
                  <UIIcon
                    src={row.isPrimary ? StarFill : StarLine}
                    size="small"
                    className={cx(styles.pinIcon, {
                      [styles.pinIconActive]: row.isPrimary,
                      [styles.pinIconDisabled]: !canPin,
                    })}
                  />
                </IconButton>
              </OptionalTooltip>
            ) : null}
          </div>
          <div className={styles.outcomeStatusCell}>
            <OutcomeStatusControl
              status={row.status}
              size="small"
              onChange={({status}) => {
                const previousStatus = getOutcomeStatus({status: row.status});
                const nextStatus = getOutcomeStatus({status});
                const changedToCompleted =
                  previousStatus !== 'completed' && nextStatus === 'completed';

                setDeliverableUrlAutoFocusDraftId(changedToCompleted ? row.draftId : null);
                if (row.type === 'deliverable') {
                  setDeliverableLinkEditDraftIds((current) =>
                    changedToCompleted
                      ? current.includes(row.draftId)
                        ? current
                        : [...current, row.draftId]
                      : nextStatus === 'completed'
                        ? current
                        : current.filter((draftId) => draftId !== row.draftId)
                  );
                }
                setRow(row.draftId, (current) => ({...current, status}));
              }}
            />
          </div>
          {showOutcomeLinkedMetricIndicatorColumn && (
            <LinkedWorkstreamIndicatorCell
              showIcon={shouldShowLinkedMetricIndicator(row)}
              itemType="outcome"
            />
          )}
          <div
            className={cx(styles.readOnlyCell, {
              [styles.outcomeTitleWithHealthScore]: showOutcomesValidation,
            })}
          >
            {showDeliverableOutcomesValidation ? (
              <DeliverableOutcomesValidationChecks workstreamId={workstreamId} />
            ) : showOutcomesValidation ? (
              <WorkstreamOutcomesValidationChecks
                activeTab={activeTab}
                workstreamId={workstreamId}
                validationMetric={row}
                isGeneratingPreview={isGeneratingOutcomesValidationPreview(row)}
                outcomesValidationPreview={getOutcomesValidationPreview(row)}
              />
            ) : null}
            <ManageOutcomeTitleField
              value={row.titleText}
              isInvalid={showNameError}
              fieldRef={(node) => setNameFieldRef(row.draftId, node)}
              onChange={(value) => {
                clearOutcomesValidationPreview(row);
                setRow(row.draftId, (current) => ({...current, titleText: value}));
              }}
              onFocus={() => setFocusedNameDraftId(row.draftId)}
              onBlur={() => {
                setFocusedNameDraftId((current) => (current === row.draftId ? null : current));
                regenerateWorkstreamOutcomesValidation(row);
              }}
              placeholder={
                row.type === 'milestone'
                  ? t('manage_outcomes_new_milestone_name').toString()
                  : t('manage_outcomes_new_deliverable_name').toString()
              }
            />
          </div>
          <div className={styles.compactFieldCell}>
            <EmployeeSelect
              selection={row.owner ?? workstreamOwner ?? undefined}
              isInvalid={hasOwnerError}
              setSelection={(employee) =>
                setRow(row.draftId, (current) => ({...current, owner: employee ?? null}))
              }
            />
          </div>
          <div className={styles.compactFieldCell}>
            <DatePickerEditor
              error={row.type !== 'metric' && isDirty(row, workstreamId) && !row.completeDate}
              date={row.completeDate}
              setDate={(date) =>
                setRow(row.draftId, (current) => ({...current, completeDate: date}))
              }
              placeholder={t('manage_outcomes_set_date').toString()}
            />
          </div>
          <div className={styles.outcomeTypeCell}>
            <OutcomeTypeLabel />
          </div>
          <div className={cx(styles.iconCell, styles.deleteActionCell)}>
            <IconButton
              variant="borderless"
              size="small"
              className={styles.deleteButton}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                handleDeleteClick(row);
              }}
              aria-label={`Delete ${row.titleText || getRowLabel(row).toLowerCase()}`}
            >
              <UIIcon src={DeleteLine} size="small" />
            </IconButton>
          </div>
        </div>
        {showSecondaryRow ? (
          <div className={styles.outcomeSecondaryRow}>
            {showUrlInput ? (
              <div className={styles.outcomeSecondaryGridLine}>
                <div className={styles.outcomeSecondaryUrlCell}>
                  <div className={styles.outcomeSecondaryNameLine}>
                    <div
                      className={cx(styles.deliverableLinkPrimaryGroup, {
                        [styles.deliverableLinkPrimaryGroupWithActions]: showDeliverableLinkActions,
                      })}
                    >
                      <Text
                        size="small"
                        color="subtle"
                        className={styles.outcomeSecondaryLabelText}
                      >
                        {t('milestone_link').toString()}
                      </Text>
                      {showDeliverableLinkActions ? (
                        <div className={styles.deliverableLinkActionGroup}>
                          <Button
                            href={normalizeExternalUrl(row.url.trim())}
                            target="_blank"
                            rel="noreferrer"
                            variant="transparent"
                            size="small"
                            hasUnderline
                            aria-label={t('view_milestone_for', {
                              name: row.titleText || getRowLabel(row),
                            }).toString()}
                            onMouseDown={(event: React.MouseEvent<HTMLAnchorElement>) => {
                              event.stopPropagation();
                            }}
                            onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
                              event.stopPropagation();
                            }}
                          >
                            {t('view').toString()}
                          </Button>
                          <Button
                            type="button"
                            variant="transparent"
                            size="small"
                            hasUnderline
                            aria-label={`Edit milestone link for ${row.titleText || getRowLabel(row)}`}
                            onClick={() => {
                              setDeliverableLinkEditDraftIds((current) =>
                                current.includes(row.draftId) ? current : [...current, row.draftId]
                              );
                              setDeliverableUrlAutoFocusDraftId(row.draftId);
                            }}
                          >
                            {t('edit').toString()}
                          </Button>
                        </div>
                      ) : (
                        <div className={styles.deliverableLinkFieldGroup}>
                          <div
                            className={cx(styles.reasonInput, styles.deliverableLinkInput)}
                            style={
                              hasDeliverableValue && !renderDeliverableUrlIconOutside
                                ? ({
                                    '--deliverable-link-icon-offset': getDeliverableUrlIconOffset(
                                      row.url
                                    ),
                                  } as CSSProperties)
                                : undefined
                            }
                          >
                            <TextInput
                              size="small"
                              autoFocus={deliverableUrlAutoFocusDraftId === row.draftId}
                              style={{width: getDeliverableUrlInputWidth(row.url)}}
                              value={row.url}
                              onChange={(event) => {
                                const nextValue = event.currentTarget.value;
                                setRow(row.draftId, (current) => ({
                                  ...current,
                                  url: nextValue,
                                }));
                              }}
                              onFocus={() => {
                                if (deliverableUrlAutoFocusDraftId === row.draftId) {
                                  setDeliverableUrlAutoFocusDraftId(null);
                                }
                              }}
                              placeholder={t('enter_url').toString()}
                            />
                            {hasDeliverableValue && !renderDeliverableUrlIconOutside ? (
                              <a
                                href={normalizeExternalUrl(row.url.trim())}
                                target="_blank"
                                rel="noreferrer"
                                aria-label={t('open_milestone_link_for', {
                                  name: row.titleText || getRowLabel(row),
                                }).toString()}
                                className={styles.deliverableLinkInlineOpenButton}
                                onMouseDown={(event) => {
                                  event.stopPropagation();
                                }}
                                onClick={(event) => {
                                  event.stopPropagation();
                                }}
                              >
                                <UIIcon src={OpenLine} size="small" />
                              </a>
                            ) : null}
                          </div>
                          {hasDeliverableValue && renderDeliverableUrlIconOutside ? (
                            <a
                              href={normalizeExternalUrl(row.url.trim())}
                              target="_blank"
                              rel="noreferrer"
                              aria-label={t('open_milestone_link_for', {
                                name: row.titleText || getRowLabel(row),
                              }).toString()}
                              className={styles.deliverableLinkTrailingOpenButton}
                              onMouseDown={(event) => {
                                event.stopPropagation();
                              }}
                              onClick={(event) => {
                                event.stopPropagation();
                              }}
                            >
                              <UIIcon src={OpenLine} size="small" />
                            </a>
                          ) : null}
                        </div>
                      )}
                    </div>
                    {deliverableUrlError ? (
                      <Text
                        size="small"
                        color="error"
                        className={cx(styles.outcomeSecondaryError, styles.deliverableLinkError)}
                      >
                        {deliverableUrlError}
                      </Text>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : null}
            <div className={styles.outcomeSecondaryContent}>
              {rowRequiresReason ? (
                <div className={styles.outcomeSecondaryGridLine}>
                  <div className={styles.outcomeSecondaryUrlCell}>
                    <div className={styles.metricSecondaryInline}>
                      <div
                        className={cx(
                          styles.reasonInput,
                          styles.metricSecondaryInput,
                          isReasonInvalid && styles.metricSecondaryInputInvalid
                        )}
                      >
                        <TextInput
                          size="small"
                          isInvalid={isReasonInvalid}
                          value={row.reason}
                          onChange={(event) => {
                            const nextValue = event.currentTarget.value;
                            setRow(row.draftId, (current) => ({
                              ...current,
                              reason: nextValue,
                            }));
                          }}
                          placeholder={t('manage_outcomes_reason_for_change').toString()}
                        />
                      </div>
                      {rowError && !hasNameError && !hasOwnerError ? (
                        <Text size="small" color="error" className={styles.metricSecondaryError}>
                          {rowError}
                        </Text>
                      ) : null}
                    </div>
                  </div>
                  <div />
                  <div />
                </div>
              ) : null}
              {!showUrlInput &&
              !rowRequiresReason &&
              rowError &&
              !hasNameError &&
              !hasOwnerError ? (
                <Text size="small" color="error">
                  {rowError}
                </Text>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  const renderReadOnlyJiraOutcomeRow = (row: ManageOutcomeReadOnlyJiraRow, attachRowRef = true) => {
    const statusChip = getJiraOutcomeStatusChipProps(row.status);
    const canDetach =
      row.isPendingElevation ||
      (row.sourceWorkstreamId != null &&
        row.sourceWorkstreamId !== workstreamId &&
        Boolean(onDetachJiraOutcome));

    const handleDetach = () => {
      if (row.isPendingElevation) {
        const pendingOutcome = pendingJiraElevations.find((item) => item.id === row.id);
        setPendingJiraElevations((current) => current.filter((item) => item.id !== row.id));
        if (pendingOutcome) {
          setElevatableJiraOutcomes((current) =>
            current.some((item) => item.id === pendingOutcome.id)
              ? current
              : [...current, pendingOutcome]
          );
        }
        return;
      }

      if (!onDetachJiraOutcome) return;
      void (async () => {
        setIsSaving(true);
        try {
          await onDetachJiraOutcome(row.id);
        } finally {
          setIsSaving(false);
        }
      })();
    };

    return (
      <div
        key={`jira-${row.id}-${row.key}`}
        ref={
          attachRowRef ? (node) => (rowRefs.current[`jira-${row.id}-${row.key}`] = node) : undefined
        }
      >
        <div className={cx(styles.tableRow, styles.outcomesColumns, styles.readOnlyJiraRow)}>
          <div className={styles.pinCell} />
          <div className={styles.outcomeStatusCell}>
            <button
              type="button"
              className={cx(styles.statusChipTrigger, styles.readOnlyControl)}
              aria-label={t(statusChip.status).toString()}
              aria-disabled="true"
              tabIndex={-1}
            >
              <StatusChip status={statusChip.status} asStatus={statusChip.asStatus} size="small" />
            </button>
          </div>
          {showOutcomeLinkedMetricIndicatorColumn && (
            <JiraLinkedWorkstreamIndicatorCell showIcon={isLinkedJiraOutcome(row, workstreamId)} />
          )}
          <div className={styles.readOnlyCell}>
            <RoadmapInitiativeLink title={row.titleText} jiraKey={row.key} lines={1} />
          </div>
          <div className={cx(styles.compactFieldCell, styles.readOnlyOwnerChipCell)}>
            {row.owner ? (
              <EmployeeChip employee={row.owner} />
            ) : (
              <Text size="small" color="subtle">
                -
              </Text>
            )}
          </div>
          <div className={styles.compactFieldCell}>
            <DatePickerEditor
              disabled
              date={row.completeDate}
              setDate={() => undefined}
              placeholder={t('manage_outcomes_set_date').toString()}
            />
          </div>
          <div className={styles.outcomeTypeCell}>
            <Button
              variant="opacity"
              size="small"
              className={cx(
                styles.dropdownTrigger,
                styles.outcomeTypeTrigger,
                styles.readOnlyJiraTypeTrigger
              )}
              tabIndex={-1}
            >
              {t('jira_initiative').toString()}
            </Button>
          </div>
          <div className={cx(styles.iconCell, styles.readOnlyActionCell)}>
            {canDetach ? (
              <button
                type="button"
                aria-label={`${t('delete').toString()} ${row.titleText}`}
                className={styles.readOnlyJiraLink}
                onClick={handleDetach}
              >
                <UIIcon src={DeleteLine} size="small" />
              </button>
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  const renderRows = (tab: VisibleManageOutcomesTab, attachRowRef = true) => {
    if (tab === 'metrics') {
      if (!attachRowRef) {
        return orderedMetricRows.map((row) => renderMetricRow(row, attachRowRef));
      }

      return (
        <DndContext
          sensors={reorderSensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={handleMetricDragEnd}
        >
          <SortableContext items={metricSortableIds} strategy={verticalListSortingStrategy}>
            {orderedMetricRows.map((row) => (
              <SortableMetricDraftRow key={row.draftId} id={row.draftId}>
                {(handleProps) => renderMetricRow(row, attachRowRef, handleProps)}
              </SortableMetricDraftRow>
            ))}
          </SortableContext>
        </DndContext>
      );
    }

    return orderedOutcomeItems.map((item) =>
      item.kind === 'draft'
        ? renderOutcomeRow(item.row, attachRowRef)
        : renderReadOnlyJiraOutcomeRow(item.row, attachRowRef)
    );
  };

  const renderTableHeader = (tab: VisibleManageOutcomesTab) => {
    if (tab === 'metrics') {
      return (
        <div
          className={cx(
            styles.tableHeader,
            styles.metricsHeader,
            styles.metricsColumns,
            showLinkedMetricIndicatorColumn && styles.metricsColumnsWithLinkedIndicator
          )}
        >
          <div className={styles.reorderHeaderCell} aria-hidden="true" />
          <div className={styles.pinHeaderCell} aria-hidden="true" />
          <div className={cx(styles.metricsHeaderStatic, styles.metricsStatusHeader)}>
            {t('status').toString()}
          </div>
          {showLinkedMetricIndicatorColumn ? (
            <div
              className={cx(styles.metricsHeaderStatic, styles.linkedMetricIndicatorHeader)}
              data-testid="linked-metric-indicator-header"
            />
          ) : null}
          <div className={cx(styles.metricsHeaderStatic, styles.metricsNameHeader)}>
            {t('name').toString()}
          </div>
          <div className={cx(styles.metricsHeaderStatic, styles.metricsDriHeader)}>
            {t('dri').toString()}
          </div>
          <div className={cx(styles.metricsHeaderStatic, styles.metricsTypeHeader)}>
            {t('type').toString()}
          </div>
          <MetricsHeaderWithTooltip
            className={styles.metricsBaselineHeaderWithInfo}
            label={t('baseline').toString()}
            tooltipText={t('baseline_tooltip').toString()}
          />
          <MetricsHeaderWithTooltip
            label={t('current').toString()}
            tooltipText={t('current_tooltip').toString()}
            maxWidth={220}
          />
          <MetricForecastHeader
            className={styles.metricForecastHeader}
            spacerClassName={styles.metricForecastHeaderSpacer}
            showValuesTooltip
          />
          <div />
        </div>
      );
    }

    return (
      <div className={cx(styles.tableHeader, styles.outcomesColumns)}>
        <div className={styles.pinHeaderCell} aria-hidden="true" />
        <div className={styles.metricsStatusHeader}>{t('status').toString()}</div>
        {showOutcomeLinkedMetricIndicatorColumn && (
          <div
            className={styles.linkedMetricIndicatorHeader}
            data-testid="linked-metric-indicator-header"
          />
        )}
        <div className={styles.metricsNameHeader}>{t('name').toString()}</div>
        <div className={styles.metricsDriHeader}>{t('dri').toString()}</div>
        <div className={styles.metricsDueHeader}>{t('eta').toString()}</div>
        <div className={styles.metricsTypeHeader}>{t('type').toString()}</div>
        <div />
      </div>
    );
  };

  const renderTabContent = (
    tab: VisibleManageOutcomesTab,
    {
      attachRowRef = true,
      className,
      style,
    }: {
      attachRowRef?: boolean;
      className?: string;
      style?: CSSProperties;
    } = {}
  ) => {
    const pickerOptions = canShowLinkableMetricSearch
      ? getFilteredPickerOptions(tab, null, linkableMetricSearchTitle)
      : [];
    const showLinkableMetricSearch = canShowLinkableMetricSearch;

    return (
      <div
        className={cx(
          styles.modalContent,
          {
            [styles.outcomesWithLinkedIndicator]:
              tab === 'outcomes' && showOutcomeLinkedMetricIndicatorColumn,
          },
          className
        )}
        style={style}
      >
        <div className={styles.tableScroller}>
          <div className={styles.table}>
            {renderTableHeader(tab)}
            {renderRows(tab, attachRowRef)}
            <div
              className={cx(
                styles.addRow,
                tab === 'metrics' ? styles.metricsColumns : styles.outcomesColumns,
                tab === 'metrics' &&
                  showLinkedMetricIndicatorColumn &&
                  styles.metricsColumnsWithLinkedIndicator
              )}
            >
              <div className={styles.addRowButtonCell}>
                <div className={styles.addRowActions}>
                  {showLinkableMetricSearch ? (
                    <WorkstreamOutcomePicker
                      value={linkableMetricSearchTitle}
                      isLoading={
                        isLoadingLinkableMetrics ||
                        (tab === 'outcomes' && isLoadingElevatableJiraOutcomes)
                      }
                      isLoadingLinkedOptions={isLoadingLinkableMetrics}
                      isLoadingJiraOptions={tab === 'outcomes' && isLoadingElevatableJiraOutcomes}
                      options={pickerOptions}
                      fieldRef={
                        attachRowRef
                          ? (node) => {
                              linkableMetricSearchRef.current = node;
                            }
                          : undefined
                      }
                      onChange={setLinkableMetricSearchTitle}
                      onFocus={() => retryFailedLoads(tab)}
                      onSelect={addSelectedPickerOption}
                      onCreate={() => addRow(tab)}
                      createType={tab === 'metrics' ? 'metric' : 'outcome'}
                      placeholder={
                        tab === 'metrics'
                          ? t('search_metrics').toString()
                          : t('search_outcomes').toString()
                      }
                    />
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Modal
        className={styles.modalWidth}
        open={open}
        onRequestClose={handleModalRequestClose}
        shouldCloseOnOverlayClick={false}
        width="large"
        isCentered
      >
        <Modal.Header>
          <Stack gap="4">
            <Box display="flex" flexDirection="row" style={{gap: 16}}>
              <Stack gap="6">
                <LabelGroup
                  verticalAlignment="center"
                  withLabel={<Eyebrow>{formatManageEyebrowLabel(activeTab)}</Eyebrow>}
                />
                <Title size={18}>
                  {workstreamName?.trim() || formatManageEyebrowLabel(activeTab)}
                </Title>
              </Stack>
            </Box>
            <Text size="small" color="subtle">
              {formatManageSubtitle(activeTab, tabCounts[activeTab])}
              {activeTab === 'metrics' &&
                hiddenMnpiMetricsCount > 0 &&
                ` ${t('hidden_mnpi_metrics_count', {count: hiddenMnpiMetricsCount})}`}
            </Text>
          </Stack>
        </Modal.Header>
        <Modal.Body className={styles.modalBody} style={{height: modalBodyHeight}}>
          <div ref={tabsRef} className={styles.tabs}>
            <div className={styles.tabsList}>
              {VISIBLE_TABS.map((tab) => (
                <button
                  key={tab}
                  type="button"
                  className={cx(styles.tabButton, {
                    [styles.tabButtonActive]: activeTab === tab,
                  })}
                  onClick={() => setActiveTab(tab)}
                >
                  {formatTabLabel(tab)}
                  {tabValidationCounts[tab] > 0 ? (
                    <Badge aria-hidden="true" className={styles.tabValidationBadge} tone="alert">
                      {tabValidationCounts[tab]}
                    </Badge>
                  ) : null}
                </button>
              ))}
            </div>
            {activeTab === 'metrics' ? (
              <MetricYearSelector
                year={selectedMetricsYear}
                years={metricsYears}
                onSelect={setSelectedMetricsYear}
                triggerVariant="opacity"
                triggerClassName={cx(styles.dropdownTrigger, styles.yearTrigger)}
                shouldPropagateClickOutsideMouseEvents
              />
            ) : null}
          </div>
          {isMeasuringTabHeights ? (
            <div className={styles.measurementLayer} aria-hidden="true">
              {VISIBLE_TABS.map((tab) => (
                <div
                  key={`measure-${tab}`}
                  ref={(node) => {
                    measurementRefs.current[tab] = node;
                  }}
                  className={cx(atoms({paddingTop: '16'}), styles.measurementPane)}
                >
                  {renderTabContent(tab, {attachRowRef: false})}
                </div>
              ))}
            </div>
          ) : null}
          {renderTabContent(activeTab, {
            className: atoms({paddingTop: '16'}),
            style: {flex: 1},
          })}
        </Modal.Body>
        <Modal.Footer>
          <div className={styles.footer}>
            <div className={styles.footerMeta}>
              <Text size="small" className={styles.muted}>
                {unsavedChangesCount > 0
                  ? t('manage_outcomes_unsaved_changes', {
                      count: unsavedChangesCount,
                    }).toString()
                  : t('manage_outcomes_no_unsaved_changes').toString()}
              </Text>
              {footerValidationSummary ? (
                <Text size="small" className={styles.footerError}>
                  {footerValidationSummary}
                </Text>
              ) : null}
            </div>
            <Cluster>
              <CancelButton onClick={handleModalRequestClose} disabled={isSaving || isPending}>
                {t('cancel')}
              </CancelButton>
              <Button
                variant="primary"
                onClick={() => void handleSave()}
                disabled={saveDisabled}
                isLoading={isSaving || isPending}
              >
                {t('save')}
              </Button>
            </Cluster>
          </div>
        </Modal.Footer>
      </Modal>
      <DeleteDraftModal
        row={deleteTarget}
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onDelete={handleDelete}
        isPending={isPending}
        draftPolicy={draftPolicy}
        workstreamId={workstreamId}
      />
    </>
  );
};

export const WorkstreamManageOutcomesTrigger = ({
  triggerVariant = 'underline',
  triggerLabel,
  ...modalProps
}: ManageOutcomesTriggerProps) => {
  const scope: ManagedTriggerScope = modalProps.initialTab === 'metrics' ? 'metrics' : 'outcomes';
  const hostIdRef = useRef<number>(nextManagedTriggerHostId++);
  useSyncExternalStore(subscribeManagedTrigger, getManagedTriggerSnapshot);

  useEffect(() => {
    const hostId = hostIdRef.current;
    registerManagedTriggerHost(scope, hostId, modalProps.workstreamId);
    return () => unregisterManagedTriggerHost(scope, hostId);
  }, [modalProps.workstreamId, scope]);

  const isActiveHost = managedTriggerActiveHost.get(scope) === hostIdRef.current;
  const isOpen = managedTriggerState.open && managedTriggerState.scope === scope;
  const label =
    triggerLabel ??
    (modalProps.initialTab === 'metrics'
      ? t('add_metric').toString()
      : modalProps.initialTab === 'milestones'
        ? t('add_milestone').toString()
        : modalProps.initialTab === 'deliverables' || modalProps.initialTab === 'outcomes'
          ? t('add_milestone').toString()
          : t('edit').toString());

  return (
    <>
      {triggerVariant === 'icon' ? (
        <IconButton
          aria-label={triggerLabel ?? t('edit').toString()}
          variant="borderless"
          size="small"
          onClick={() => openManagedTriggerModal(scope, hostIdRef.current, modalProps)}
        >
          <UIIcon src={EditLine} size="small" />
        </IconButton>
      ) : triggerVariant === 'edit' ? (
        <Button
          variant="transparent"
          size="small"
          onClick={() => openManagedTriggerModal(scope, hostIdRef.current, modalProps)}
        >
          <Text color="subtle" size="xsmall">
            {triggerLabel ?? t('edit').toString()}
          </Text>
        </Button>
      ) : triggerVariant === 'chip' ? (
        <Chip
          size="small"
          onClick={() => openManagedTriggerModal(scope, hostIdRef.current, modalProps)}
          className={cx(
            styles.managedTriggerChip,
            atoms({
              backgroundColor: 'transparent',
              borderColor: 'Border Subtle',
              borderStyle: 'Solid',
              borderWidth: '1',
            })
          )}
          style={{display: 'inline-flex', maxWidth: '100%', width: 'auto'}}
        >
          <Chip.IconAccessory>
            <UIIcon src={AddLine} className={atoms({color: 'Border Base'})} />
          </Chip.IconAccessory>
          <Chip.Content>
            <Text color="subtle" size="small">
              {label}
            </Text>
          </Chip.Content>
        </Chip>
      ) : (
        <Button
          type="button"
          variant="transparent"
          size="small"
          hasUnderline
          onClick={() => openManagedTriggerModal(scope, hostIdRef.current, modalProps)}
        >
          {label}
        </Button>
      )}
      {isActiveHost ? (
        <WorkstreamManageOutcomesModal
          open={isOpen}
          onClose={closeManagedTriggerModal}
          {...modalProps}
          initialTab={isOpen ? managedTriggerState.initialTab : modalProps.initialTab}
          onActiveTabChange={
            isOpen ? (tab) => updateManagedTriggerTab(scope, tab) : modalProps.onActiveTabChange
          }
          focusMetricId={isOpen ? managedTriggerState.focusMetricId : modalProps.focusMetricId}
          workstreamOwner={
            isOpen ? managedTriggerState.workstreamOwner : modalProps.workstreamOwner
          }
          metricPeriodOptions={
            isOpen ? managedTriggerState.metricPeriodOptions : modalProps.metricPeriodOptions
          }
        />
      ) : null}
    </>
  );
};
